import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { X, Phone, MessageSquare, FileText, Mail, MapPin, Car, ChevronRight, Download, Send, Building2, Trash2, Plus, Archive, Clock, Calendar, DollarSign, Search, AlertCircle, Gift, CreditCard, Tag, ChevronDown, Wallet, Loader2, MoreVertical, Package, ArrowUpRight, Users, RefreshCw } from 'lucide-react';
import { useModalZIndex, ModalStackProvider } from '../contexts/ModalStackContext';
import CustomerBillingTab from './invoices/CustomerBillingTab';
import { useNavigate } from 'react-router-dom';
import {
  Customer,
  CustomerVehicle,
  CustomerTag,
  updateCustomer,
  createCustomerVehicle,
  updateCustomerVehicle,
  deleteCustomerVehicle,
  deleteCustomer,
  getCustomer,
  getCustomerTags,
  createCustomerTag,
  deleteCustomerTag,
  updateCustomerTags,
  parseCustomerAdditionalAddresses,
  parseCustomerAdditionalAddressesForForm,
  sanitizeCustomerAdditionalAddressesForSave,
} from '../lib/customers';
import { DEFAULT_TERM_OPTIONS, termLabel } from '../lib/invoices/invoiceTerms';
import CustomerAdditionalAddressesFields from './customer/CustomerAdditionalAddressesFields';
import CustomerAddressCards from './CustomerAddressCards';
import CustomerSavedCardPanel from './customer/CustomerSavedCardPanel';
import { CustomerContainersCard } from './CustomerContainersCard';
import RefundSheet from './refunds/RefundSheet';
import { refundCustomer, describeRefundOutcome } from '../lib/refunds';
import { CustomerPricingCard } from './CustomerPricingCard';
import { CustomerRentalsCard } from './orders/RentalsTab';
import { getBookingsByCustomer, Booking } from '../lib/bookings';
import { getProposalsByCustomer, Proposal } from '../lib/proposals';
import { getOrders, Order } from '../lib/purchaseOrders';
import {
  CustomerOrder,
  fetchCustomerOrdersForCustomer,
  orderPaymentState,
} from '../lib/customerOrders';
import { OrderRow } from './orders/OrderRow';
import PurchaseOrderDetailModal from './PurchaseOrderDetailModal';
import ProposalDetailsModal from './ProposalDetailsModal';
import CreateVehicleModal from './CreateVehicleModal';
import { getCustomerMemberships } from '../lib/memberships';
import { useBusiness } from '../contexts/BusinessContext';
import { useCall } from '../contexts/CallContext';
import { useAppSystem } from '../hooks/useAppSystem';
import { useMessagingChannels } from '../hooks/useMessagingChannels';
import { openNativeCall } from '../lib/contactActions';
import EmptyState from './EmptyState';
import AddressAutocomplete from './AddressAutocomplete';
import { supabase } from '../lib/supabase';
import FieldQuoteDetailModal from './FieldQuoteDetailModal';
import BookingInfoModal from './BookingInfoModal';
import CrmEmbeddedOpportunityModal from './admin-dashboard/CrmEmbeddedOpportunityModal';

interface CustomerOpportunity {
  id: number;
  title: string;
  pipeline_id: number;
  stage_id: number;
  value: number;
  status: string;
  source: string | null;
  created_at: string;
}

interface CustomerFieldQuote {
  id: string;
  status: string;
  is_measurement: boolean;
  start_date: string | null;
  start_time: string | null;
  end_date: string | null;
  end_time: string | null;
  field_quote_address: string | null;
  created_at: string;
  project_title: string | null;
}
import EditSubscriptionModal from './EditSubscriptionModal';
import toast from 'react-hot-toast';

// Tabs will be dynamically built based on installed apps

// Lazy: CustomerOrdersTab statically imports this modal, so a static import
// back at OrderDetailModal would create a require cycle. Dynamic import breaks it.
const OrderDetailModalLazy = React.lazy(() =>
  import('./orders/CustomerOrdersTab').then(m => ({ default: m.OrderDetailModal }))
);

// Same filter set as the Orders page/widget, plus "All" (default here — a
// single customer's history is small enough to scan whole).
type StoreOrderFilter = CustomerOrder['status'] | 'all' | 'active' | 'unpaid';

const STORE_ORDER_FILTERS: Array<{ id: StoreOrderFilter; label: string }> = [
  { id: 'all', label: 'All' },
  { id: 'active', label: 'Active' },
  { id: 'new', label: 'New' },
  { id: 'confirmed', label: 'Confirmed' },
  { id: 'out_for_delivery', label: 'Out / Ready' },
  { id: 'delivered', label: 'Delivered' },
  { id: 'unpaid', label: 'Unpaid' },
  { id: 'cancelled', label: 'Cancelled' },
];

interface CustomerDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  customer: Customer | null;
  onUpdate: () => void;
  /** Override base z-index when this modal must stack above another high-z modal (e.g. ProposalDetailsModal). */
  zIndex?: number;
}

export default function CustomerDetailsModal({ isOpen, onClose, customer, onUpdate, zIndex: zIndexProp }: CustomerDetailsModalProps) {
  // Resolve effective z-index: explicit prop wins; otherwise the nearest ModalStackProvider
  // (e.g. when opened from inside another modal) supplies a higher z-index automatically;
  // otherwise we fall back to the historical default.
  const resolvedZIndex = useModalZIndex(100120);
  const zIndex = zIndexProp ?? resolvedZIndex;
  const { selectedBusiness } = useBusiness();
  const { coreApps, installedAddons } = useAppSystem();
  const navigate = useNavigate();
  const { startCall, deviceStatus } = useCall();
  const { twilioConnected } = useMessagingChannels();
  const [isStartingCall, setIsStartingCall] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  // Delivery addresses only matter when the online store shop is enabled —
  // businesses without a shop never see the address book section.
  const [storeShopEnabled, setStoreShopEnabled] = useState(false);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<'overview' | 'vehicles' | 'jobs' | 'leads' | 'billing' | 'proposals' | 'gift_cards' | 'memberships' | 'wallet' | 'orders' | 'subscriptions'>('overview');
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState<Partial<Customer>>({});
  const [isAddingVehicle, setIsAddingVehicle] = useState(false);
  const [customerBookings, setCustomerBookings] = useState<Booking[]>([]);
  const [loadingBookings, setLoadingBookings] = useState(false);
  const [customerFieldQuotes, setCustomerFieldQuotes] = useState<CustomerFieldQuote[]>([]);
  const [selectedFieldQuoteId, setSelectedFieldQuoteId] = useState<string | null>(null);
  const [isFieldQuoteDetailOpen, setIsFieldQuoteDetailOpen] = useState(false);
  const [selectedBookingForDetail, setSelectedBookingForDetail] = useState<Booking | null>(null);
  const [isBookingDetailOpen, setIsBookingDetailOpen] = useState(false);
  const [customerProposals, setCustomerProposals] = useState<Proposal[]>([]);
  const [loadingProposals, setLoadingProposals] = useState(false);
  const [customerOpportunities, setCustomerOpportunities] = useState<CustomerOpportunity[]>([]);
  const [loadingOpportunities, setLoadingOpportunities] = useState(false);
  // pipeline_id → { name, stages } for resolving pipeline/stage labels on lead cards
  const [pipelineInfo, setPipelineInfo] = useState<Map<number, { name: string; stages: { id: number; name: string }[] }>>(new Map());
  const [selectedOpportunityId, setSelectedOpportunityId] = useState<number | null>(null);
  const [selectedProposalForModal, setSelectedProposalForModal] = useState<Proposal | null>(null);
  const [customerGiftCards, setCustomerGiftCards] = useState<any[]>([]);
  const [loadingGiftCards, setLoadingGiftCards] = useState(false);
  const [customerTags, setCustomerTags] = useState<CustomerTag[]>([]);
  const [loadingTags, setLoadingTags] = useState(false);
  const [customerMemberships, setCustomerMemberships] = useState<any[]>([]);
  const [loadingMemberships, setLoadingMemberships] = useState(false);
  const [expandedMemberships, setExpandedMemberships] = useState<Set<string>>(new Set());
  const [walletBalance, setWalletBalance] = useState<number>(0);
  const [loadingWallet, setLoadingWallet] = useState(false);
  const [supplierOrders, setSupplierOrders] = useState<Order[]>([]);
  const [loadingSupplierOrders, setLoadingSupplierOrders] = useState(false);
  const [selectedOrderForModal, setSelectedOrderForModal] = useState<Order | null>(null);
  const [customerSubscriptions, setCustomerSubscriptions] = useState<any[]>([]);
  const [loadingSubscriptions, setLoadingSubscriptions] = useState(false);
  const [selectedSubscriptionForModal, setSelectedSubscriptionForModal] = useState<any | null>(null);
  const [savingPreferredContact, setSavingPreferredContact] = useState(false);
  const [savingInvoiceOnly, setSavingInvoiceOnly] = useState(false);
  const [savingVerificationExempt, setSavingVerificationExempt] = useState(false);
  const [isAddingCredit, setIsAddingCredit] = useState(false);
  const [adjustMode, setAdjustMode] = useState<'add' | 'deduct'>('add');
  // Wallet payout (money actually leaves) — amount entry, then the refund sheet.
  const [isPayoutOpen, setIsPayoutOpen] = useState(false);
  const [isPayoutSheetOpen, setIsPayoutSheetOpen] = useState(false);
  const [payoutAmount, setPayoutAmount] = useState('');
  const [creditAmount, setCreditAmount] = useState<string>('');
  const [creditNote, setCreditNote] = useState<string>('');
  const [walletTransactions, setWalletTransactions] = useState<any[]>([]);
  const [loadingTransactions, setLoadingTransactions] = useState(false);
  const [showPoints, setShowPoints] = useState(false);
  const [pointsBalance, setPointsBalance] = useState(0);
  const [pointsEntries, setPointsEntries] = useState<Array<{ id: string; points: number; reason: string; note: string | null; created_at: string }>>([]);
  const [loadingPoints, setLoadingPoints] = useState(false);
  const [isAdjustingPoints, setIsAdjustingPoints] = useState(false);
  const [pointsMode, setPointsMode] = useState<'add' | 'deduct'>('add');
  const [pointsAmount, setPointsAmount] = useState('');
  const [pointsNote, setPointsNote] = useState('');
  const [isTagsDropdownOpen, setIsTagsDropdownOpen] = useState(false);
  const [newTagName, setNewTagName] = useState('');
  const [newTagColor, setNewTagColor] = useState('#3B82F6');
  const [isCreatingTag, setIsCreatingTag] = useState(false);
  const [assignedTagIds, setAssignedTagIds] = useState<string[]>([]);
  const [initialTagIds, setInitialTagIds] = useState<string[]>([]);
  const tagsDropdownRef = React.useRef<HTMLDivElement>(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuRef = React.useRef<HTMLDivElement>(null);


  // Track if we just saved tags to prevent overwriting
  const justSavedTagsRef = React.useRef(false);
  const contentScrollRef = React.useRef<HTMLDivElement>(null);

  // Show the Delivery Addresses section only when the business's online store
  // shop is enabled (online_store_settings.is_enabled).
  useEffect(() => {
    setStoreShopEnabled(false);
    if (!isOpen || !selectedBusiness?.id || !supabase) return;
    let stale = false;
    supabase
      .from('online_store_settings')
      .select('is_enabled')
      .eq('business_id', selectedBusiness.id)
      .maybeSingle()
      .then(({ data }) => {
        if (!stale) setStoreShopEnabled(data?.is_enabled === true);
      });
    return () => { stale = true; };
  }, [isOpen, selectedBusiness?.id]);

  // Close menu when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsMenuOpen(false);
      }
    };

    if (isMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMenuOpen]);

  React.useEffect(() => {
    if (isOpen && customer) {
      setIsAnimating(true);
      // For suppliers only Overview + Orders; start on overview. If switching to a customer from supplier, leave orders tab so we show overview.
      if (customer.is_supplier) {
        setActiveTab('overview');
      } else {
        setActiveTab((prev) => (prev === 'orders' ? 'overview' : prev));
      }
      // Only update formData if we haven't just saved tags (to preserve local updates)
      if (!justSavedTagsRef.current) {
        setFormData(customer);
        // Update wallet balance from customer data
        if (customer.wallet !== undefined) {
          setWalletBalance(parseFloat(String(customer.wallet)) || 0);
        }
      } else {
        // Reset the flag after a brief delay
        setTimeout(() => {
          justSavedTagsRef.current = false;
        }, 100);
      }
    }
  }, [isOpen, customer?.id]); // Only depend on customer.id, not the whole customer object

  // Add a new effect to fetch fresh customer data when modal is opened
  React.useEffect(() => {
    if (isOpen && customer && !justSavedTagsRef.current) {
      // Refresh the customer data by fetching it again
      const refreshCustomerData = async () => {
        try {
          const freshCustomer = await getCustomer(customer.id);
          setFormData(freshCustomer);
          markBasicSaved(freshCustomer);
          // Update wallet balance from fresh customer data
          if (freshCustomer.wallet !== undefined) {
            setWalletBalance(parseFloat(String(freshCustomer.wallet)) || 0);
          }
        } catch (err) {
          console.error('Error refreshing customer data:', err);
        }
      };
      
      refreshCustomerData();
    }
  }, [isOpen, customer?.id]);

  // Fetch customer bookings when modal opens or customer changes
  const loadCustomerBookings = async () => {
    if (!customer?.id || !selectedBusiness?.id) return;

    try {
      setLoadingBookings(true);
      const bookings = await getBookingsByCustomer(selectedBusiness.id, customer.id);
      setCustomerBookings(bookings);
    } catch (err) {
      console.error('Error loading customer bookings:', err);
    } finally {
      setLoadingBookings(false);
    }
  };

  // Field quotes / measurements are tracked separately from bookings but should
  // appear in the same Jobs tab so the full job history for a customer is visible.
  const loadCustomerFieldQuotes = async () => {
    if (!customer?.id || !selectedBusiness?.id || !supabase) return;

    try {
      const { data, error } = await supabase
        .from('project_field_quotes')
        .select(`
          id,
          status,
          is_measurement,
          start_date,
          start_time,
          end_date,
          end_time,
          field_quote_address,
          created_at,
          project:projects(title)
        `)
        .eq('business_id', selectedBusiness.id)
        .eq('customer_id', customer.id)
        .order('start_date', { ascending: false, nullsFirst: false });

      if (error) throw error;

      const rows: CustomerFieldQuote[] = (data ?? []).map((row: any) => {
        const project = Array.isArray(row.project) ? row.project[0] : row.project;
        return {
          id: row.id,
          status: row.status,
          is_measurement: !!row.is_measurement,
          start_date: row.start_date ?? null,
          start_time: row.start_time ?? null,
          end_date: row.end_date ?? null,
          end_time: row.end_time ?? null,
          field_quote_address: row.field_quote_address ?? null,
          created_at: row.created_at,
          project_title: project?.title ?? null,
        };
      });

      setCustomerFieldQuotes(rows);
    } catch (err) {
      console.error('Error loading customer field quotes:', err);
      setCustomerFieldQuotes([]);
    }
  };

  // Fetch customer memberships when modal opens or customer changes  
  const loadCustomerMemberships = async () => {
    if (!customer?.id) return;

    try {
      setLoadingMemberships(true);
      const memberships = await getCustomerMemberships(customer.id);
      setCustomerMemberships(memberships);
    } catch (err) {
      console.error('Error loading customer memberships:', err);
    } finally {
      setLoadingMemberships(false);
    }
  };

  // Fetch customer proposals when modal opens or customer changes
  const loadCustomerProposals = async () => {
    if (!customer?.id || !selectedBusiness?.id) return;

    try {
      setLoadingProposals(true);
      const proposals = await getProposalsByCustomer(selectedBusiness.id, customer.id);
      setCustomerProposals(proposals);
    } catch (err) {
      console.error('Error loading customer proposals:', err);
    } finally {
      setLoadingProposals(false);
    }
  };

  // Fetch customer gift cards when modal opens or customer changes
  const loadCustomerGiftCards = async () => {
    if (!customer?.id || !selectedBusiness?.id) return;

    try {
      setLoadingGiftCards(true);
      const { data, error } = await supabase
        .from('gift_cards')
        .select('*')
        .eq('business_id', selectedBusiness.id)
        .eq('purchaser_customer_id', customer.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setCustomerGiftCards(data || []);
    } catch (err) {
      console.error('Error loading customer gift cards:', err);
    } finally {
      setLoadingGiftCards(false);
    }
  };

  // Load wallet balance when customer changes
  const loadWalletBalance = async () => {
    if (!customer?.id || !supabase) return;

    try {
      setLoadingWallet(true);
      const { data, error } = await supabase
        .from('customers')
        .select('wallet')
        .eq('id', customer.id)
        .single();

      if (error) throw error;
      setWalletBalance(parseFloat(data?.wallet || '0') || 0);
    } catch (err) {
      console.error('Error loading wallet balance:', err);
    } finally {
      setLoadingWallet(false);
    }
  };

  const loadWalletTransactions = async () => {
    if (!customer?.id || !selectedBusiness?.id || !supabase) return;
    try {
      setLoadingTransactions(true);
      const { data, error } = await supabase
        .from('wallet_transactions')
        .select('*')
        .eq('customer_id', customer.id)
        .eq('business_id', selectedBusiness.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setWalletTransactions(data || []);
    } catch (err) {
      console.error('Error loading wallet transactions:', err);
    } finally {
      setLoadingTransactions(false);
    }
  };

  const loadRewardPoints = async () => {
    if (!customer?.id || !selectedBusiness?.id || !supabase) return;
    try {
      setLoadingPoints(true);
      const [{ data: program }, balanceResult, { data: entries, error: entriesError }] = await Promise.all([
        supabase
          .from('reward_programs')
          .select('id')
          .eq('business_id', selectedBusiness.id)
          .maybeSingle(),
        supabase.rpc('reward_points_balance', { p_customer_id: customer.id }),
        supabase
          .from('reward_point_entries')
          .select('id, points, reason, note, created_at')
          .eq('customer_id', customer.id)
          .eq('business_id', selectedBusiness.id)
          .order('created_at', { ascending: false })
          .limit(8),
      ]);
      if (entriesError) throw entriesError;
      const balance = typeof balanceResult.data === 'number' ? balanceResult.data : Number(balanceResult.data || 0);
      setShowPoints(Boolean(program) || balance !== 0);
      setPointsBalance(Number.isFinite(balance) ? balance : 0);
      setPointsEntries(entries || []);
    } catch (err) {
      console.error('Error loading reward points:', err);
    } finally {
      setLoadingPoints(false);
    }
  };

  const handleAdjustPoints = async () => {
    if (!customer?.id || !supabase) return;
    const amount = Number(pointsAmount);
    if (!Number.isInteger(amount) || amount <= 0) {
      toast.error('Enter a whole number of points');
      return;
    }
    const signed = pointsMode === 'deduct' ? -amount : amount;
    if (pointsMode === 'deduct' && amount > pointsBalance) {
      toast.error(`Cannot deduct more than the current balance (${pointsBalance.toLocaleString()} points)`);
      return;
    }
    try {
      setLoadingPoints(true);
      const { data, error } = await supabase.rpc('adjust_reward_points', {
        p_customer_id: customer.id,
        p_points: signed,
        p_note: pointsNote.trim() || null,
      });
      if (error) throw error;
      setPointsBalance(typeof data === 'number' ? data : Number(data || 0));
      setPointsAmount('');
      setPointsNote('');
      setIsAdjustingPoints(false);
      loadRewardPoints();
      toast.success(pointsMode === 'deduct' ? `Removed ${amount.toLocaleString()} points` : `Added ${amount.toLocaleString()} points`);
    } catch (err) {
      console.error('Error adjusting points:', err);
      const message = err && typeof err === 'object' && 'message' in err ? String((err as { message: string }).message) : 'Failed to adjust points';
      toast.error(message);
    } finally {
      setLoadingPoints(false);
    }
  };

  // Manually adjust wallet balance (add or deduct) via the audited RPC.
  const handleAdjustWallet = async () => {
    if (!customer?.id || !selectedBusiness?.id || !supabase) return;

    const amount = parseFloat(creditAmount);
    if (!amount || amount <= 0) {
      toast.error('Please enter a valid amount greater than 0');
      return;
    }
    const signedAmount = adjustMode === 'deduct' ? -amount : amount;
    if (adjustMode === 'deduct' && amount > walletBalance) {
      toast.error(`Cannot deduct more than the current balance ($${walletBalance.toFixed(2)})`);
      return;
    }

    try {
      setLoadingWallet(true);

      const { data: newBalance, error } = await supabase.rpc('adjust_wallet_balance', {
        p_customer_id: customer.id,
        p_amount: signedAmount,
        p_note: creditNote || null,
      });

      if (error) throw error;

      setWalletBalance(parseFloat(String(newBalance)) || 0);
      setCreditAmount('');
      setCreditNote('');
      setIsAddingCredit(false);
      loadWalletTransactions();

      toast.success(
        adjustMode === 'deduct'
          ? `Deducted $${amount.toFixed(2)} from wallet`
          : `Added $${amount.toFixed(2)} to wallet`
      );
      onUpdate(); // Refresh customer data
    } catch (err) {
      console.error('Error adjusting wallet balance:', err);
      toast.error(err instanceof Error ? err.message : 'Failed to adjust wallet balance');
    } finally {
      setLoadingWallet(false);
    }
  };

  // Fetch PO history when viewing a supplier
  const loadSupplierOrders = async () => {
    if (!customer?.id || !selectedBusiness?.id) return;

    try {
      setLoadingSupplierOrders(true);
      const orders = await getOrders(selectedBusiness.id, { supplierId: customer.id });
      setSupplierOrders(orders);
      setSelectedOrderForModal((prev) => (prev ? orders.find((o) => o.id === prev.id) ?? prev : null));
    } catch (err) {
      console.error('Error loading supplier orders:', err);
    } finally {
      setLoadingSupplierOrders(false);
    }
  };

  // Supplier view: only Overview + Orders
  const isSupplier = React.useMemo(() => Boolean(customer?.is_supplier), [customer?.is_supplier]);

  // Check if gift cards app is installed
  const isGiftCardsAppInstalled = React.useMemo(() => {
    const allApps = [...coreApps, ...installedAddons];
    return allApps.some(app => app.app?.slug === 'gift-cards' && app.status === 'active');
  }, [coreApps, installedAddons]);

  const isServicesAppInstalled = React.useMemo(() => {
    const allApps = [...coreApps, ...installedAddons];
    return allApps.some(app => app.app?.slug === 'services' && app.status === 'active');
  }, [coreApps, installedAddons]);

  const isCrmAppInstalled = React.useMemo(() => {
    const allApps = [...coreApps, ...installedAddons];
    return allApps.some(app => app.app?.slug === 'crm' && app.status === 'active');
  }, [coreApps, installedAddons]);

  const isInventoryAppInstalled = React.useMemo(() => {
    const allApps = [...coreApps, ...installedAddons];
    return allApps.some(app => app.app?.slug === 'inventory' && app.status === 'active');
  }, [coreApps, installedAddons]);

  // Subscriptions feature toggle from the Services app settings (default off).
  const [subscriptionsEnabled, setSubscriptionsEnabled] = useState(false);
  useEffect(() => {
    if (!selectedBusiness?.id || !supabase || !isServicesAppInstalled) {
      setSubscriptionsEnabled(false);
      return;
    }
    supabase
      .rpc('get_app_setting', {
        p_business_id: selectedBusiness.id,
        p_app_slug: 'services',
        p_setting_key: 'enable_subscriptions',
        p_default_value: false,
      })
      .then(({ data }) => {
        if (data !== null) setSubscriptionsEnabled(Boolean(data));
      });
  }, [selectedBusiness?.id, isServicesAppInstalled]);

  const stripeConnectReadyForSavedCards = React.useMemo(
    () =>
      Boolean(
        selectedBusiness?.stripe_connect_account_id &&
          selectedBusiness?.stripe_connect_charges_enabled &&
          selectedBusiness?.online_payments_enabled !== false,
      ),
    [
      selectedBusiness?.stripe_connect_account_id,
      selectedBusiness?.stripe_connect_charges_enabled,
      selectedBusiness?.online_payments_enabled,
    ],
  );

  const loadCustomerSubscriptions = async () => {
    if (!customer?.id || !selectedBusiness?.id) return;
    try {
      setLoadingSubscriptions(true);
      const { data, error } = await supabase
        .from('subscription_services')
        .select('*')
        .eq('business_id', selectedBusiness.id)
        .eq('customer_id', customer.id)
        .order('created_at', { ascending: false });

      if (!error && data) {
        setCustomerSubscriptions(data);
      }
    } catch (err) {
      console.error('Error loading customer subscriptions:', err);
    } finally {
      setLoadingSubscriptions(false);
    }
  };

  // Build dynamic tabs: for suppliers only Overview + Orders; for customers use app-based tabs
  const tabs = React.useMemo(() => {
    if (isSupplier) {
      return [
        { id: 'overview', label: 'Overview' },
        { id: 'orders', label: 'Orders' }
      ];
    }
    const isHomeServices = selectedBusiness?.industry === 'Home Services';
    const baseTabs: Array<{ id: string; label: string }> = [
      { id: 'overview', label: 'Overview' },
      ...(!isHomeServices ? [{ id: 'vehicles', label: 'Vehicles' }] : []),
      { id: 'jobs', label: 'Jobs' },
      // Store orders (customer_orders) — only meaningful with the Inventory app.
      ...(isInventoryAppInstalled ? [{ id: 'orders', label: 'Orders' }] : []),
      ...(isCrmAppInstalled ? [{ id: 'leads', label: 'Leads' }] : []),
      { id: 'billing', label: 'Billing' },
      { id: 'proposals', label: 'Proposals' },
      { id: 'wallet', label: 'Wallet' }
    ];

    if (isGiftCardsAppInstalled) {
      baseTabs.push({ id: 'gift_cards', label: 'Gift Cards' });
    }

    // Subscriptions tab only when the business has actually enabled
    // subscriptions in the Services app settings (not merely installed it).
    if (isServicesAppInstalled && subscriptionsEnabled) {
      baseTabs.push({ id: 'subscriptions', label: 'Subscriptions' });
    }

    return baseTabs;
  }, [isSupplier, isGiftCardsAppInstalled, isServicesAppInstalled, isCrmAppInstalled, isInventoryAppInstalled, subscriptionsEnabled, selectedBusiness?.industry]);

  // ── Store orders (customer_orders) for the Orders tab ──────────────────────
  const [customerStoreOrders, setCustomerStoreOrders] = useState<CustomerOrder[]>([]);
  const [loadingStoreOrders, setLoadingStoreOrders] = useState(false);
  const [selectedStoreOrder, setSelectedStoreOrder] = useState<CustomerOrder | null>(null);
  const [storeOrderFilter, setStoreOrderFilter] = useState<StoreOrderFilter>('all');

  const filteredStoreOrders = React.useMemo(() => {
    if (storeOrderFilter === 'all') return customerStoreOrders;
    if (storeOrderFilter === 'active') {
      return customerStoreOrders.filter(o => o.status !== 'delivered' && o.status !== 'cancelled');
    }
    if (storeOrderFilter === 'unpaid') {
      return customerStoreOrders.filter(o => {
        const pay = orderPaymentState(o);
        return o.status !== 'cancelled' && (pay === 'unpaid' || pay === 'invoice_due' || pay === 'partial');
      });
    }
    return customerStoreOrders.filter(o => o.status === storeOrderFilter);
  }, [customerStoreOrders, storeOrderFilter]);

  const loadCustomerStoreOrders = React.useCallback(async () => {
    if (!customer?.id || !selectedBusiness?.id) return;
    try {
      setLoadingStoreOrders(true);
      setCustomerStoreOrders(await fetchCustomerOrdersForCustomer(selectedBusiness.id, customer.id));
    } catch (err) {
      console.error('Error loading customer store orders:', err);
    } finally {
      setLoadingStoreOrders(false);
    }
  }, [customer?.id, selectedBusiness?.id]);

  useEffect(() => {
    if (isOpen && isInventoryAppInstalled && !isSupplier) {
      loadCustomerStoreOrders();
    }
  }, [isOpen, isInventoryAppInstalled, isSupplier, loadCustomerStoreOrders]);

  // ── Lifetime value: everything actually collected on this customer's invoices
  // (all sources — bookings, orders, product sales; partial payments count what
  // was paid so far; voided invoices excluded).
  const [lifetimeValueCents, setLifetimeValueCents] = useState<number | null>(null);

  useEffect(() => {
    if (!isOpen || isSupplier || !customer?.id || !selectedBusiness?.id || !supabase) {
      setLifetimeValueCents(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const { data, error } = await supabase
          .from('invoices')
          .select('amount_paid_cents')
          .eq('business_id', selectedBusiness.id)
          .eq('customer_id', customer.id)
          .neq('status', 'void');
        if (error) throw error;
        if (!cancelled) {
          setLifetimeValueCents((data || []).reduce((sum, r) => sum + (Number(r.amount_paid_cents) || 0), 0));
        }
      } catch (err) {
        console.error('Error loading customer lifetime value:', err);
      }
    })();
    return () => { cancelled = true; };
  }, [isOpen, isSupplier, customer?.id, selectedBusiness?.id]);

  // Load the customer's CRM opportunities plus pipeline/stage labels.
  const loadCustomerOpportunities = async () => {
    if (!customer?.id || !selectedBusiness?.id || !supabase) return;
    try {
      setLoadingOpportunities(true);
      const [oppsResult, pipelinesResult] = await Promise.all([
        supabase
          .from('pipeline_opportunities')
          .select('id, title, pipeline_id, stage_id, value, status, source, created_at')
          .eq('business_id', selectedBusiness.id)
          .eq('customer_id', customer.id)
          .order('created_at', { ascending: false }),
        supabase
          .from('pipelines')
          .select('id, pipeline_name, stages')
          .eq('business_id', selectedBusiness.id),
      ]);

      if (!oppsResult.error && oppsResult.data) {
        setCustomerOpportunities(oppsResult.data as CustomerOpportunity[]);
      }
      if (!pipelinesResult.error && pipelinesResult.data) {
        setPipelineInfo(new Map(
          pipelinesResult.data.map((p: any) => [
            p.id,
            { name: p.pipeline_name, stages: Array.isArray(p.stages) ? p.stages : [] },
          ])
        ));
      }
    } catch (err) {
      console.error('Error loading customer opportunities:', err);
    } finally {
      setLoadingOpportunities(false);
    }
  };

  // Load customer tags
  const loadCustomerTags = async () => {
    if (!selectedBusiness?.id) return;

    try {
      setLoadingTags(true);
      const tags = await getCustomerTags(selectedBusiness.id);
      setCustomerTags(tags);
    } catch (err) {
      console.error('Error loading customer tags:', err);
    } finally {
      setLoadingTags(false);
    }
  };

  // Initialize assigned tags from customer
  React.useEffect(() => {
    if (customer?.Tags) {
      const tagIds = Array.isArray(customer.Tags) 
        ? customer.Tags.map((tag: any) => typeof tag === 'string' ? tag : tag.id)
        : [];
      setAssignedTagIds(tagIds);
      setInitialTagIds(tagIds);
    } else {
      setAssignedTagIds([]);
      setInitialTagIds([]);
    }
  }, [customer?.Tags]);

  // Reset initial tags when dropdown opens
  React.useEffect(() => {
    if (isTagsDropdownOpen) {
      setInitialTagIds([...assignedTagIds]);
    }
  }, [isTagsDropdownOpen]);


  // Handle click outside to close dropdown
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (tagsDropdownRef.current && !tagsDropdownRef.current.contains(event.target as Node)) {
        setIsTagsDropdownOpen(false);
      }
    };

    if (isTagsDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isTagsDropdownOpen]);

  React.useEffect(() => {
    if (isOpen && customer?.id && selectedBusiness?.id) {
      if (customer.is_supplier) {
        loadSupplierOrders();
      } else {
        loadCustomerBookings();
        loadCustomerFieldQuotes();
        loadCustomerProposals();
        loadCustomerTags();
        loadWalletBalance();
        loadWalletTransactions();
        loadRewardPoints();
        if (isGiftCardsAppInstalled) {
          loadCustomerGiftCards();
        }
        if (isServicesAppInstalled) {
          loadCustomerSubscriptions();
        }
        if (isCrmAppInstalled) {
          loadCustomerOpportunities();
        }
      }
    }
  }, [isOpen, customer?.id, selectedBusiness?.id, isGiftCardsAppInstalled, isServicesAppInstalled, isCrmAppInstalled, customer?.is_supplier]);

  React.useEffect(() => {
    if (contentScrollRef.current) {
      contentScrollRef.current.scrollTop = 0;
    }
  }, [activeTab]);

  const handleTransitionEnd = () => {
    if (!isOpen) {
      setIsAnimating(false);
      setError(null);
      setIsAddingVehicle(false);
    }
  };

  const handleClose = () => {
    setError(null);
    onClose();
  };

  // Call / other flows dispatch this so stacked modals clear before the dial pad opens.
  useEffect(() => {
    const handleCloseAllModals = () => {
      if (isOpen) onClose();
    };
    window.addEventListener('closeAllModals', handleCloseAllModals);
    return () => window.removeEventListener('closeAllModals', handleCloseAllModals);
  }, [isOpen, onClose]);

  const handlePreferredContactMethodChange = async (value: 'email' | 'sms' | 'both') => {
    if (!customer?.id) return;
    try {
      setSavingPreferredContact(true);
      setError(null);
      await updateCustomer(customer.id, { preferred_contact_method: value });
      setFormData((prev) => ({ ...prev, preferred_contact_method: value }));
      onUpdate();
      toast.success('Preferred contact method updated');
    } catch (err) {
      console.error('Error updating preferred contact method:', err);
      setError(err instanceof Error ? err.message : 'Failed to update');
    } finally {
      setSavingPreferredContact(false);
    }
  };

  const handleInvoiceOnlyToggle = async (value: boolean) => {
    if (!customer?.id) return;
    try {
      setSavingInvoiceOnly(true);
      setError(null);
      await updateCustomer(customer.id, { invoice_only: value });
      setFormData((prev: any) => ({ ...prev, invoice_only: value }));
      onUpdate();
      toast.success(value ? 'Customer set to invoice-only checkout' : 'Invoice-only checkout turned off');
    } catch (err) {
      console.error('Error updating invoice-only setting:', err);
      setError(err instanceof Error ? err.message : 'Failed to update');
    } finally {
      setSavingInvoiceOnly(false);
    }
  };

  const handleVerificationExemptToggle = async (value: boolean) => {
    if (!customer?.id) return;
    try {
      setSavingVerificationExempt(true);
      setError(null);
      await updateCustomer(customer.id, { portal_verification_exempt: value } as any);
      setFormData((prev: any) => ({ ...prev, portal_verification_exempt: value }));
      onUpdate();
      toast.success(value
        ? 'Email verification waived — their next account signup or password reset skips the code'
        : 'Email verification required again');
    } catch (err) {
      console.error('Error updating verification exemption:', err);
      setError(err instanceof Error ? err.message : 'Failed to update');
    } finally {
      setSavingVerificationExempt(false);
    }
  };

  const handlePaymentTermsChange = async (value: string) => {
    if (!customer?.id) return;
    const terms = value === 'default' ? null : value;
    try {
      setError(null);
      await updateCustomer(customer.id, { default_payment_terms: terms } as any);
      setFormData((prev: any) => ({ ...prev, default_payment_terms: terms }));
      onUpdate();
      toast.success(terms ? `Payment terms set to ${termLabel(terms)}` : 'Payment terms reset to business default');
    } catch (err) {
      console.error('Error updating payment terms:', err);
      setError(err instanceof Error ? err.message : 'Failed to update');
    }
  };

  // ── Live saving for Basic Information ──────────────────────────────────────
  // Every field is always editable. A change is written when the field loses
  // focus (or Enter is pressed), with a short debounce as a backstop, and any
  // pending change is flushed when the modal closes. Only the fields that
  // actually differ from the last saved snapshot are sent.
  const BASIC_FIELDS = ['first_name', 'last_name', 'company', 'email', 'phone', 'address', 'lat', 'lng', 'additional_addresses'] as const;
  type BasicField = typeof BASIC_FIELDS[number];
  const savedBasicRef = useRef<Partial<Customer>>({});
  const formDataRef = useRef<Partial<Customer>>(formData);
  formDataRef.current = formData;
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const saveChainRef = useRef<Promise<void>>(Promise.resolve());
  const onUpdateRef = useRef(onUpdate);
  onUpdateRef.current = onUpdate;
  const [basicSaveState, setBasicSaveState] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [basicFieldErrors, setBasicFieldErrors] = useState<Partial<Record<BasicField, string>>>({});

  /** Remember what the server has, so later diffs only send real changes. */
  const markBasicSaved = useCallback((data: Partial<Customer>) => {
    const snap: Partial<Customer> = {};
    for (const key of BASIC_FIELDS) (snap as Record<string, unknown>)[key] = (data as Record<string, unknown>)[key];
    savedBasicRef.current = snap;
  }, []);

  const sameValue = (a: unknown, b: unknown) => {
    const norm = (v: unknown) => (v === undefined || v === null ? '' : typeof v === 'object' ? JSON.stringify(v) : String(v));
    return norm(a) === norm(b);
  };

  const flushBasicSave = useCallback(async () => {
    if (!customer?.id) return;
    if (saveTimerRef.current) { clearTimeout(saveTimerRef.current); saveTimerRef.current = null; }
    const current = formDataRef.current;
    if (current?.id && current.id !== customer.id) return;
    const patch: Record<string, unknown> = {};
    const errors: Partial<Record<BasicField, string>> = {};
    for (const key of BASIC_FIELDS) {
      const next = (current as Record<string, unknown>)[key];
      const prev = (savedBasicRef.current as Record<string, unknown>)[key];
      if (sameValue(next, prev)) continue;
      if (key === 'first_name' && !String(next ?? '').trim()) { errors.first_name = 'First name is required'; continue; }
      if (key === 'last_name' && !String(next ?? '').trim()) { errors.last_name = 'Last name is required'; continue; }
      if (key === 'phone' && !String(next ?? '').trim()) { errors.phone = 'Phone number is required'; continue; }
      patch[key] = key === 'additional_addresses'
        ? sanitizeCustomerAdditionalAddressesForSave(parseCustomerAdditionalAddresses(next))
        : next;
    }
    setBasicFieldErrors(errors);
    if (Object.keys(patch).length === 0) return;

    const run = async () => {
      try {
        setBasicSaveState('saving');
        setError(null);
        await updateCustomer(customer.id, patch as Partial<Customer>);
        savedBasicRef.current = { ...savedBasicRef.current, ...(patch as Partial<Customer>) };
        setBasicSaveState('saved');
        onUpdateRef.current();
      } catch (err) {
        console.error('Error saving customer field:', err);
        setBasicSaveState('error');
        setError(err instanceof Error ? err.message : 'Failed to update customer');
      }
    };
    saveChainRef.current = saveChainRef.current.then(run, run);
    await saveChainRef.current;
  }, [customer?.id]);

  /** Change a field now and schedule the save. */
  const editBasic = useCallback((patch: Partial<Customer>) => {
    setFormData((prev) => ({ ...prev, ...patch }));
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => { void flushBasicSave(); }, 1200);
  }, [flushBasicSave]);

  // Keep the saved snapshot in step whenever the form is loaded from the server.
  useEffect(() => {
    if (formData?.id && formData.id !== savedBasicRef.current.id) {
      markBasicSaved(formData);
      (savedBasicRef.current as Partial<Customer>).id = formData.id;
      setBasicSaveState('idle');
      setBasicFieldErrors({});
    }
  }, [formData?.id, markBasicSaved]); // eslint-disable-line react-hooks/exhaustive-deps

  // "Saved" fades back to idle.
  useEffect(() => {
    if (basicSaveState !== 'saved') return;
    const t = setTimeout(() => setBasicSaveState('idle'), 2000);
    return () => clearTimeout(t);
  }, [basicSaveState]);

  // Flush anything pending when the modal closes or the customer changes.
  useEffect(() => {
    if (isOpen) return;
    void flushBasicSave();
  }, [isOpen, flushBasicSave]);
  useEffect(() => () => { if (saveTimerRef.current) { clearTimeout(saveTimerRef.current); void flushBasicSave(); } }, [flushBasicSave]);

  const blurOnEnter = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') (e.currentTarget as HTMLInputElement).blur();
  };

  const handleDeleteVehicle = async (vehicleId: string) => {
    if (!window.confirm('Are you sure you want to delete this vehicle?')) return;

    try {
      setLoading(true);
      setError(null);

      await deleteCustomerVehicle(vehicleId);
      onUpdate();
    } catch (err) {
      console.error('Error deleting vehicle:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete vehicle');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTag = async () => {
    if (!selectedBusiness?.id || !newTagName.trim()) return;

    try {
      setIsCreatingTag(true);
      const newTag = await createCustomerTag(selectedBusiness.id, newTagName, newTagColor);
      // Add the new tag to the list and sort by name using functional update
      setCustomerTags(prevTags => {
        const updatedTags = [...prevTags, newTag].sort((a, b) => 
          a.name.localeCompare(b.name)
        );
        return updatedTags;
      });
      setNewTagName('');
      setNewTagColor('#3B82F6');
    } catch (err) {
      console.error('Error creating tag:', err);
      setError(err instanceof Error ? err.message : 'Failed to create tag');
    } finally {
      setIsCreatingTag(false);
    }
  };

  const handleDeleteTag = async (tagId: string) => {
    if (!window.confirm('Are you sure you want to delete this tag? This will remove it from all customers.')) return;

    try {
      setLoading(true);
      await deleteCustomerTag(tagId);
      setCustomerTags(customerTags.filter(tag => tag.id !== tagId));
      setAssignedTagIds(assignedTagIds.filter(id => id !== tagId));
    } catch (err) {
      console.error('Error deleting tag:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete tag');
    } finally {
      setLoading(false);
    }
  };

  // Remove a single assigned tag directly from the displayed pill (persists immediately).
  const handleRemoveAssignedTag = async (tagId: string) => {
    if (!customer?.id || !tagId) return;

    const newIds = assignedTagIds.filter((id) => id !== tagId);
    try {
      setLoading(true);
      setError(null);
      const updatedCustomer = await updateCustomerTags(customer.id, newIds);

      // Prevent the init effect from overwriting our optimistic update.
      justSavedTagsRef.current = true;
      setAssignedTagIds(newIds);
      setInitialTagIds([...newIds]);
      setFormData((prev) => ({
        ...prev,
        ...updatedCustomer,
        Tags: updatedCustomer.Tags || [],
      }));

      await onUpdate();
    } catch (err) {
      console.error('Error removing tag:', err);
      setError(err instanceof Error ? err.message : 'Failed to remove tag');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveTags = async (ids: string[] = assignedTagIds, opts?: { keepOpen?: boolean }) => {
    if (!customer?.id) return;

    try {
      setLoading(true);
      setError(null);
      const updatedCustomer = await updateCustomerTags(customer.id, ids);
      if (!opts?.keepOpen) setIsTagsDropdownOpen(false);
      
      // Set flag to prevent effect from overwriting our update
      justSavedTagsRef.current = true;
      
      // Update formData with the fresh customer data including Tags
      // This ensures the tags display updates immediately
      setFormData(prev => ({
        ...prev,
        ...updatedCustomer,
        Tags: updatedCustomer.Tags || []
      }));
      
      // Update initialTagIds to match the saved state
      setInitialTagIds([...ids]);
      
      // Call onUpdate to refresh the parent component's customer data
      // This will also update the selectedCustomer in the parent
      await onUpdate();
    } catch (err) {
      console.error('Error updating tags:', err);
      setError(err instanceof Error ? err.message : 'Failed to update tags');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'scheduled':
        return 'bg-blue-100/80 text-blue-700';
      case 'in_progress':
        return 'bg-yellow-100/80 text-yellow-700';
      case 'completed':
        return 'bg-green-100/80 text-green-700';
      case 'cancelled':
        return 'bg-red-100/80 text-red-700';
      default:
        return 'bg-gray-100/80 text-gray-700';
    }
  };

  const getProposalStatusColor = (status: string) => {
    switch (status) {
      case 'draft':
        return 'bg-gray-100/80 text-gray-700';
      case 'sent':
        return 'bg-blue-100/80 text-blue-700';
      case 'viewed':
        return 'bg-blue-100/80 text-blue-700';
      case 'approved':
        return 'bg-green-100/80 text-green-700';
      case 'declined':
        return 'bg-red-100/80 text-red-700';
      default:
        return 'bg-gray-100/80 text-gray-700';
    }
  };

  /** List/parent `customer` may omit columns (e.g. address); `formData` is refreshed via getCustomer. */
  const basicInfoForDisplay = React.useMemo((): Customer | null => {
    if (!customer) return null;
    if (formData?.id === customer.id) {
      return { ...customer, ...formData } as Customer;
    }
    return customer;
  }, [customer, formData]);

  if (!isOpen && !isAnimating) return null;
  if (!customer) return null;

  const modalContent = (
    <>
      {/* Modal Container - parent wraps both backdrop and panel for proper iOS hit-testing */}
      <div
        className={`fixed inset-0 transition-opacity duration-200 ${
          isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        style={{ zIndex }}
        onMouseDown={(e) => { if (e.target === e.currentTarget) handleClose(); }}
        onTransitionEnd={handleTransitionEnd}
      >
        {/* Visual backdrop */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            backdropFilter: 'blur(8px)',
            WebkitBackdropFilter: 'blur(8px)',
          }}
        />

        {/* Modal Content */}
        <div
          className={`fixed inset-y-0 right-0 w-full md:w-[700px] lg:w-[800px] bg-white shadow-2xl transform transition-transform duration-200 ease-out ${
            isOpen ? 'translate-x-0' : 'translate-x-full'
          }`}
          style={{
            zIndex: zIndex + 1,
            fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif',
            paddingTop: 'env(safe-area-inset-top)',
            paddingBottom: 'env(safe-area-inset-bottom)',
          }}
          onMouseDown={(e) => e.stopPropagation()}
        >
        <div className="h-full flex flex-col overflow-hidden">
          {/* Header */}
          <div className="bg-gray-50 border-b border-gray-200 px-6 py-6 relative">
            <button
              onClick={handleClose}
              className="absolute top-4 right-4 h-8 w-8 flex items-center justify-center rounded-full hover:bg-gray-100 active:bg-gray-200 active:scale-95 transition-all duration-150 touch-manipulation"
              style={{ WebkitTapHighlightColor: 'transparent' }}
            >
              <X className="h-5 w-5 text-gray-500" />
            </button>
            {!isSupplier && lifetimeValueCents !== null && (
              <div className="absolute top-14 right-4 text-right">
                <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">Lifetime Value</p>
                <p className="text-[15px] font-bold text-emerald-600" style={{ letterSpacing: '-0.01em' }}>
                  ${(lifetimeValueCents / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </p>
              </div>
            )}
            <div className="text-center">
              {/* iMessage-style contact header: big circle avatar with initials */}
              <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-b from-gray-300 to-gray-400 text-white shadow-sm">
                <span className="text-[24px] font-semibold leading-none">
                  {`${(basicInfoForDisplay?.first_name || '').charAt(0)}${(basicInfoForDisplay?.last_name || '').charAt(0)}`.toUpperCase() || '?'}
                </span>
              </div>
              <h2 className="mt-2 text-xl font-bold text-gray-900" style={{ letterSpacing: '-0.02em' }}>
                {basicInfoForDisplay?.first_name} {basicInfoForDisplay?.last_name}
              </h2>
              {basicInfoForDisplay?.company && (
                <p className="mt-0.5 text-sm font-medium text-gray-500">{basicInfoForDisplay.company}</p>
              )}
              <div className="flex items-center justify-center mt-1">
                <div className="h-2 w-2 rounded-full bg-green-400 mr-2" />
                <span className="text-sm text-gray-500">{isSupplier ? 'Active Supplier' : 'Active Customer'}</span>
              </div>

              {/* Quick-action circles (call / message / mail), like iMessage */}
              <div className="mt-3 flex items-center justify-center gap-3">
                <button
                  type="button"
                  disabled={!basicInfoForDisplay?.phone || isStartingCall}
                  onClick={async () => {
                    if (!basicInfoForDisplay?.phone || isStartingCall) return;
                    const phoneNumber = basicInfoForDisplay.phone;
                    if (twilioConnected) {
                      if (deviceStatus !== 'ready') {
                        toast.error('Phone is still connecting. Try again in a moment.');
                        return;
                      }
                      setIsStartingCall(true);
                      try {
                        // Tap-to-call: start immediately (do not open the dial pad).
                        await startCall(phoneNumber, {
                          id: customer?.id || basicInfoForDisplay?.id,
                          first_name: basicInfoForDisplay.first_name,
                          last_name: basicInfoForDisplay.last_name,
                        });
                        handleClose();
                      } catch (err: any) {
                        toast.error(err?.message || 'Failed to start call');
                      } finally {
                        setIsStartingCall(false);
                      }
                      return;
                    }
                    openNativeCall(phoneNumber);
                  }}
                  className="flex h-10 w-10 items-center justify-center rounded-full bg-white border border-gray-200 shadow-sm text-blue-600 transition-all duration-150 active:scale-95 hover:bg-blue-50 disabled:opacity-35 disabled:pointer-events-none touch-manipulation"
                  style={{ WebkitTapHighlightColor: 'transparent' }}
                  title="Call"
                  aria-label="Call customer"
                >
                  {isStartingCall ? (
                    <Loader2 className="h-[18px] w-[18px] animate-spin" />
                  ) : (
                    <Phone className="h-[18px] w-[18px]" />
                  )}
                </button>
                <button
                  type="button"
                  disabled={!basicInfoForDisplay?.phone && !basicInfoForDisplay?.email}
                  onClick={() => {
                    if (!customer?.id) return;
                    handleClose();
                    navigate(`/messages?customerId=${customer.id}`);
                  }}
                  className="flex h-10 w-10 items-center justify-center rounded-full bg-white border border-gray-200 shadow-sm text-blue-600 transition-all duration-150 active:scale-95 hover:bg-blue-50 disabled:opacity-35 disabled:pointer-events-none touch-manipulation"
                  style={{ WebkitTapHighlightColor: 'transparent' }}
                  title="Message"
                  aria-label="Message customer"
                >
                  <MessageSquare className="h-[18px] w-[18px]" />
                </button>
                <button
                  type="button"
                  disabled={!basicInfoForDisplay?.email}
                  onClick={() => {
                    if (!customer?.id) return;
                    handleClose();
                    navigate(`/messages?customerId=${customer.id}`);
                  }}
                  className="flex h-10 w-10 items-center justify-center rounded-full bg-white border border-gray-200 shadow-sm text-blue-600 transition-all duration-150 active:scale-95 hover:bg-blue-50 disabled:opacity-35 disabled:pointer-events-none touch-manipulation"
                  style={{ WebkitTapHighlightColor: 'transparent' }}
                  title="Email"
                  aria-label="Email customer"
                >
                  <Mail className="h-[18px] w-[18px]" />
                </button>
              </div>
            </div>
            {/* 3-dots menu button */}
            <div className="absolute top-4 left-4" ref={menuRef}>
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="inline-flex items-center justify-center w-10 h-10 text-gray-600 hover:text-gray-900 hover:bg-gray-100 active:bg-gray-200 rounded-full transition-all duration-150 active:scale-[0.95] touch-manipulation"
                style={{ WebkitTapHighlightColor: 'transparent' }}
                aria-label="More options"
              >
                <MoreVertical className="h-5 w-5" />
              </button>
              
              {/* Dropdown menu */}
              {isMenuOpen && (
                <div className="absolute top-12 left-0 z-50 w-48 bg-white rounded-xl shadow-lg border border-gray-200 py-1">
                  <button
                    onClick={async () => {
                      setIsMenuOpen(false);
                      if (window.confirm('Are you sure you want to delete this customer? They will be marked as inactive but their data will be preserved.')) {
                        try {
                          console.log('🗑️ Deleting customer:', customer?.id);
                          setLoading(true);
                          
                          // Pass the complete customer data with status update to avoid validation errors
                          await updateCustomer(customer.id, {
                            ...customer,
                            status: 'inactive'
                          });
                          console.log('✅ Customer marked as inactive successfully');
                          onUpdate();
                          onClose();
                        } catch (err) {
                          console.error('❌ Error marking customer as inactive:', err);
                          alert('Failed to delete customer: ' + (err instanceof Error ? err.message : 'Unknown error'));
                        } finally {
                          setLoading(false);
                        }
                      }
                    }}
                    disabled={loading}
                    className="w-full px-4 py-3 text-left text-sm font-medium text-red-600 hover:bg-red-50 active:bg-red-100 transition-colors flex items-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{ WebkitTapHighlightColor: 'transparent' }}
                  >
                    <Trash2 className="h-4 w-4" />
                    {loading ? 'Deleting...' : 'Delete Customer'}
                  </button>
                </div>
              )}
            </div>
          </div>

            {/* Tabs */}
            <div className="bg-white border-b border-gray-200 px-6">
              <nav className="flex space-x-8 overflow-x-auto" aria-label="Tabs" style={{ WebkitOverflowScrolling: 'touch' }}>
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id as 'overview' | 'vehicles' | 'jobs' | 'proposals' | 'gift_cards' | 'wallet' | 'orders' | 'subscriptions')}
                    className={`py-4 border-b-2 text-sm font-semibold whitespace-nowrap transition-colors ${
                      activeTab === tab.id
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </nav>
            </div>

            {/* Content */}
            <div ref={contentScrollRef} className="flex-1 overflow-y-auto bg-white" style={{ 
              WebkitOverflowScrolling: 'touch',
              paddingBottom: 'env(safe-area-inset-bottom, 0px)'
            }}>
              <div className="py-6 px-6" style={{
                paddingBottom: 'calc(1rem + env(safe-area-inset-bottom, 0px))'
              }}>
                {error && (
                  <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm">
                    <div className="flex items-center">
                      <AlertCircle className="h-4 w-4 mr-2 flex-shrink-0" />
                      {error}
                    </div>
                  </div>
                )}

                <div className="space-y-6">
                  {activeTab === 'overview' && (
                    <div className="bg-white rounded-[12px] border border-gray-200/60 shadow-[0_1px_2px_rgba(0,0,0,0.05)] overflow-hidden">
                      <div className="bg-gray-50 border-b border-gray-200 px-6 py-4 flex justify-between items-center">
                        <h3 className="text-lg font-semibold text-gray-900">Basic Information</h3>
                        <span className="text-xs font-medium text-gray-500 inline-flex items-center gap-1.5 min-h-[20px]" aria-live="polite">
                          {basicSaveState === 'saving' && (<><Loader2 className="h-3.5 w-3.5 animate-spin" /> Saving…</>)}
                          {basicSaveState === 'saved' && <span className="text-green-600">Saved</span>}
                          {basicSaveState === 'error' && <span className="text-red-600">Not saved</span>}
                          {basicSaveState === 'idle' && <span className="text-gray-400">Changes save as you go</span>}
                        </span>
                      </div>
                      <div className="p-6" onBlur={() => { void flushBasicSave(); }}>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                          <div>
                            <label htmlFor="first_name" className="block text-sm font-medium text-gray-700 mb-2">First Name</label>
                            <input
                              type="text"
                              id="first_name"
                              value={formData.first_name || ''}
                              onChange={(e) => editBasic({ first_name: e.target.value })}
                              onKeyDown={blurOnEnter}
                              className={`w-full px-4 py-3 text-base bg-white border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-150 ${basicFieldErrors.first_name ? 'border-red-400' : 'border-gray-300'}`}
                              style={{ fontSize: '16px' }}
                            />
                            {basicFieldErrors.first_name && <p className="mt-1 text-xs text-red-600">{basicFieldErrors.first_name}</p>}
                          </div>
                          <div>
                            <label htmlFor="last_name" className="block text-sm font-medium text-gray-700 mb-2">Last Name</label>
                            <input
                              type="text"
                              id="last_name"
                              value={formData.last_name || ''}
                              onChange={(e) => editBasic({ last_name: e.target.value })}
                              onKeyDown={blurOnEnter}
                              className={`w-full px-4 py-3 text-base bg-white border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-150 ${basicFieldErrors.last_name ? 'border-red-400' : 'border-gray-300'}`}
                              style={{ fontSize: '16px' }}
                            />
                            {basicFieldErrors.last_name && <p className="mt-1 text-xs text-red-600">{basicFieldErrors.last_name}</p>}
                          </div>
                          <div className="sm:col-span-2">
                            <label htmlFor="company" className="block text-sm font-medium text-gray-700 mb-2">Company</label>
                            <input
                              type="text"
                              id="company"
                              placeholder="Company / business name (optional)"
                              value={formData.company || ''}
                              onChange={(e) => editBasic({ company: e.target.value })}
                              onKeyDown={blurOnEnter}
                              className="w-full px-4 py-3 text-base bg-white border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-150"
                              style={{ fontSize: '16px' }}
                            />
                          </div>
                          <div>
                            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                            <input
                              type="email"
                              id="email"
                              value={formData.email || ''}
                              onChange={(e) => editBasic({ email: e.target.value })}
                              onKeyDown={blurOnEnter}
                              className="w-full px-4 py-3 text-base bg-white border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-150"
                              style={{ fontSize: '16px' }}
                            />
                          </div>
                          <div>
                            <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">Phone</label>
                            <input
                              type="tel"
                              id="phone"
                              value={formData.phone || ''}
                              onChange={(e) => editBasic({ phone: e.target.value })}
                              onKeyDown={blurOnEnter}
                              className={`w-full px-4 py-3 text-base bg-white border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-150 ${basicFieldErrors.phone ? 'border-red-400' : 'border-gray-300'}`}
                              style={{ fontSize: '16px' }}
                            />
                            {basicFieldErrors.phone && <p className="mt-1 text-xs text-red-600">{basicFieldErrors.phone}</p>}
                          </div>
                          <div className="sm:col-span-2">
                            <label htmlFor="address" className="block text-sm font-medium text-gray-700 mb-2">Address</label>
                            <AddressAutocomplete
                              value={formData.address || ''}
                              onChange={(address) => editBasic({ address })}
                              onSelect={(address) => {
                                editBasic({ address: address.formatted, lat: address.lat, lng: address.lng });
                                void flushBasicSave();
                              }}
                              placeholder="Enter address (optional)"
                              className="w-full px-4 py-3 text-base bg-white border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-150"
                            />
                          </div>
                          <div className="sm:col-span-2" style={{ overflow: 'visible' }}>
                            <CustomerAdditionalAddressesFields
                              modalAutocomplete={false}
                              entries={parseCustomerAdditionalAddressesForForm(formData.additional_addresses)}
                              onChange={(entries) => editBasic({ additional_addresses: entries })}
                            />
                          </div>
                          {/* Delivery addresses (address book — source of truth for
                              orders/online checkout; the Address above stays the
                              customer's physical address). Only shown when the
                              business actually runs an online store shop. */}
                          {storeShopEnabled && customer?.id && selectedBusiness?.id && (
                            <div className="sm:col-span-2">
                              <div className="text-sm font-medium text-gray-500 mb-2">Delivery Addresses</div>
                              <CustomerAddressCards
                                businessId={selectedBusiness.id}
                                customerId={customer.id}
                                mode="manage"
                              />
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {activeTab === 'overview' && isSupplier && (() => {
                    const hasEmail = Boolean((formData.email ?? customer?.email)?.trim());
                    const hasPhone = Boolean((formData.phone ?? customer?.phone)?.trim());
                    const options: { value: 'email' | 'sms' | 'both'; label: string }[] = [];
                    if (hasEmail) options.push({ value: 'email', label: 'Email' });
                    if (hasPhone) options.push({ value: 'sms', label: 'SMS' });
                    if (hasEmail && hasPhone) options.push({ value: 'both', label: 'Both' });
                    const current = (formData.preferred_contact_method ?? customer?.preferred_contact_method) ?? null;
                    if (options.length === 0) return null;
                    return (
                      <div className="bg-white rounded-[12px] border border-gray-200/60 shadow-[0_1px_2px_rgba(0,0,0,0.05)] overflow-hidden">
                        <div className="bg-gray-50 border-b border-gray-200 px-6 py-4">
                          <h3 className="text-lg font-semibold text-gray-900">Preferred contact method</h3>
                          <p className="text-sm text-gray-500 mt-0.5">How you prefer to reach this supplier</p>
                        </div>
                        <div className="p-6">
                          <fieldset className="space-y-3" disabled={savingPreferredContact}>
                            {options.map((opt) => (
                              <label
                                key={opt.value}
                                className="flex items-center gap-3 p-3 rounded-xl border border-gray-200/60 cursor-pointer hover:bg-gray-50/80 transition-colors has-[:checked]:border-blue-500 has-[:checked]:bg-blue-50/50"
                              >
                                <input
                                  type="radio"
                                  name="preferred_contact_method"
                                  value={opt.value}
                                  checked={current === opt.value}
                                  onChange={() => handlePreferredContactMethodChange(opt.value)}
                                  className="h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                                />
                                <span className="text-sm font-medium text-gray-900">{opt.label}</span>
                                {savingPreferredContact && current === opt.value && (
                                  <Loader2 className="h-4 w-4 animate-spin text-gray-400 ml-auto" />
                                )}
                              </label>
                            ))}
                          </fieldset>
                        </div>
                      </div>
                    );
                  })()}

                  {activeTab === 'overview' && !isSupplier && (
                    <div className="bg-white rounded-[12px] border border-gray-200/60 shadow-[0_1px_2px_rgba(0,0,0,0.05)]" style={{ overflow: 'visible' }}>
                      <div className="bg-gray-50 border-b border-gray-200 px-6 py-4 flex justify-between items-center relative">
                        <h3 className="text-lg font-semibold text-gray-900">Tags</h3>
                        <div className="relative" ref={tagsDropdownRef} style={{ zIndex: 1000 }}>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setIsTagsDropdownOpen(!isTagsDropdownOpen);
                            }}
                            className="inline-flex items-center justify-center px-4 py-2 text-sm font-semibold text-blue-600 hover:text-blue-700 bg-blue-50 hover:bg-blue-100 active:bg-blue-200 rounded-xl transition-all duration-150 active:scale-[0.98] touch-manipulation"
                            style={{ WebkitTapHighlightColor: 'transparent' }}
                          >
                            <Plus className="h-4 w-4 mr-1.5" />
                            Add Tags
                          </button>
                          {isTagsDropdownOpen && createPortal(
                            <>
                              <div 
                                className="fixed inset-0"
                                style={{ zIndex: zIndex + 2 }}
                                onMouseDown={(e) => {
                                  // Only close if clicking directly on the backdrop, not on children
                                  if (e.target === e.currentTarget) {
                                    setIsTagsDropdownOpen(false);
                                  }
                                }}
                              />
                              <div 
                                className="fixed bg-white border border-gray-200/60 rounded-[12px] shadow-2xl w-80 max-w-[90vw] overflow-y-auto"
                                style={(() => {
                                  const isMobile = window.innerWidth < 768; // md breakpoint
                                  const rect = tagsDropdownRef.current?.getBoundingClientRect();
                                  if (isMobile || !rect) {
                                    // Center on mobile (or when we can't measure the button)
                                    return {
                                      zIndex: zIndex + 3,
                                      left: '50%',
                                      top: '50%',
                                      transform: 'translate(-50%, -50%)',
                                      maxHeight: '80vh',
                                    };
                                  }

                                  // Desktop: anchor to the button, but flip above and clamp the
                                  // height so the popover never spills off-screen.
                                  const gap = 8;
                                  const margin = 8;
                                  const vh = window.innerHeight;
                                  const right = window.innerWidth - rect.right;
                                  const spaceBelow = vh - rect.bottom - gap - margin;
                                  const spaceAbove = rect.top - gap - margin;
                                  const openUp = spaceBelow < 320 && spaceAbove > spaceBelow;

                                  if (openUp) {
                                    return {
                                      zIndex: zIndex + 3,
                                      bottom: `${vh - rect.top + gap}px`,
                                      right: `${right}px`,
                                      maxHeight: `${Math.max(200, spaceAbove)}px`,
                                    };
                                  }
                                  return {
                                    zIndex: zIndex + 3,
                                    top: `${rect.bottom + gap}px`,
                                    right: `${right}px`,
                                    maxHeight: `${Math.max(200, spaceBelow)}px`,
                                  };
                                })()}
                                onMouseDown={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                }}
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                }}
                              >
                                <div className="p-4 border-b border-gray-200" onClick={(e) => e.stopPropagation()}>
                                  <h4 className="text-sm font-semibold text-gray-900 mb-3">Create New Tag</h4>
                                  <div className="space-y-3">
                                    <input
                                      type="text"
                                      placeholder="Tag name"
                                      value={newTagName}
                                      onChange={(e) => setNewTagName(e.target.value)}
                                      onClick={(e) => e.stopPropagation()}
                                      onFocus={(e) => e.stopPropagation()}
                                      onMouseDown={(e) => {
                                        e.stopPropagation();
                                      }}
                                      onKeyDown={(e) => {
                                        e.stopPropagation();
                                        if (e.key === 'Enter' && newTagName.trim()) {
                                          e.preventDefault();
                                          handleCreateTag();
                                        }
                                      }}
                                      className="w-full px-4 py-3 text-sm border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-150"
                                      style={{ fontSize: '16px' }}
                                    />
                                    <div className="flex items-center gap-2">
                                      <input
                                        type="color"
                                        value={newTagColor}
                                        onChange={(e) => setNewTagColor(e.target.value)}
                                        onClick={(e) => e.stopPropagation()}
                                        onMouseDown={(e) => e.stopPropagation()}
                                        className="w-10 h-10 border border-gray-300 rounded cursor-pointer"
                                      />
                                      <button
                                        type="button"
                                        onMouseDown={(e) => {
                                          e.preventDefault();
                                          e.stopPropagation();
                                        }}
                                        onClick={(e) => {
                                          e.preventDefault();
                                          e.stopPropagation();
                                          handleCreateTag();
                                        }}
                                        disabled={!newTagName.trim() || isCreatingTag}
                                        className="flex-1 px-4 py-3 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 active:bg-blue-800 rounded-xl transition-all duration-150 active:scale-[0.98] touch-manipulation disabled:opacity-50"
                                        style={{ WebkitTapHighlightColor: 'transparent' }}
                                      >
                                        {isCreatingTag ? 'Creating...' : 'Create Tag'}
                                      </button>
                                    </div>
                                  </div>
                                </div>
                                <div className="p-4" onClick={(e) => e.stopPropagation()}>
                                  <h4 className="text-sm font-semibold text-gray-900 mb-3">Select Tags</h4>
                                  {loadingTags ? (
                                    <div className="text-center py-4 text-sm text-gray-500">Loading tags...</div>
                                  ) : customerTags.length === 0 ? (
                                    <div className="text-center py-4 text-sm text-gray-500">No tags available. Create one above.</div>
                                  ) : (
                                    <div className="space-y-2 max-h-64 overflow-y-auto">
                                    {customerTags.map((tag) => (
                                      <div 
                                        key={tag.id} 
                                        className="flex items-center justify-between p-2 hover:bg-gray-50 rounded-lg"
                                        onClick={(e) => e.stopPropagation()}
                                        onMouseDown={(e) => e.stopPropagation()}
                                      >
                                        <div className="flex items-center gap-2 flex-1">
                                          <input
                                            type="checkbox"
                                            checked={assignedTagIds.includes(tag.id)}
                                            disabled={loading}
                                            onChange={(e) => {
                                              e.stopPropagation();
                                              const next = e.target.checked
                                                ? [...assignedTagIds, tag.id]
                                                : assignedTagIds.filter(id => id !== tag.id);
                                              setAssignedTagIds(next);
                                              void handleSaveTags(next, { keepOpen: true });
                                            }}
                                            onClick={(e) => e.stopPropagation()}
                                            onMouseDown={(e) => e.stopPropagation()}
                                            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                          />
                                            <div
                                              className="w-4 h-4 rounded-full"
                                              style={{ backgroundColor: tag.color || '#3B82F6' }}
                                            />
                                            <span className="text-sm text-gray-900">{tag.name}</span>
                                          </div>
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleDeleteTag(tag.id);
                                          }}
                                          className="p-1 text-red-600 hover:text-red-800 hover:bg-red-50 rounded transition-colors"
                                          title="Delete tag"
                                        >
                                          <Trash2 className="h-4 w-4" />
                                        </button>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                  <div className="mt-4 pt-4 border-t border-gray-200 flex items-center gap-2">
                                    <span className="flex-1 text-xs text-gray-500 inline-flex items-center gap-1.5">
                                      {loading ? (<><Loader2 className="h-3.5 w-3.5 animate-spin" /> Saving…</>) : 'Tags save as you tick them'}
                                    </span>
                                    <button
                                      type="button"
                                      onMouseDown={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                      }}
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        setIsTagsDropdownOpen(false);
                                        setNewTagName('');
                                        setNewTagColor('#3B82F6');
                                      }}
                                      className="px-4 py-3 text-sm font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200 active:bg-gray-300 rounded-xl transition-all duration-150 active:scale-[0.98] touch-manipulation"
                                      style={{ WebkitTapHighlightColor: 'transparent' }}
                                    >
                                      Done
                                    </button>
                                  </div>
                                </div>
                              </div>
                            </>,
                            document.body
                          )}
                        </div>
                      </div>
                      <div className="p-6">
                        {(() => {
                          // Always prioritize formData.Tags over customer.Tags for immediate updates
                          // formData is updated immediately after saving, while customer prop may lag
                          const tagsToDisplay = (formData?.Tags && formData.Tags.length > 0) 
                            ? formData.Tags 
                            : (customer?.Tags || []);
                          return tagsToDisplay && Array.isArray(tagsToDisplay) && tagsToDisplay.length > 0 ? (
                            <div className="flex flex-wrap gap-2">
                              {tagsToDisplay.map((tag: any, index: number) => {
                              // Handle both object format {id, name, color} and string format (tag ID)
                              const tagData = typeof tag === 'object' && tag !== null 
                                ? tag 
                                : customerTags.find(t => t.id === tag);
                              
                              if (!tagData) return null;
                              
                              const tagName = typeof tagData === 'object' ? tagData.name : customerTags.find(t => t.id === tagData)?.name || '';
                              const tagColor = typeof tagData === 'object' ? (tagData.color || '#3B82F6') : (customerTags.find(t => t.id === tagData)?.color || '#3B82F6');
                              
                              const removeId = typeof tagData === 'object' ? tagData.id : tagData;
                              return (
                                <span
                                  key={typeof tagData === 'object' ? tagData.id : tagData || index}
                                  className="inline-flex items-center gap-2 pl-3 pr-2 py-1.5 rounded-full text-sm font-medium"
                                  style={{
                                    backgroundColor: `${tagColor}20`,
                                    color: tagColor,
                                    border: `1px solid ${tagColor}40`
                                  }}
                                >
                                  <div
                                    className="w-2 h-2 rounded-full"
                                    style={{ backgroundColor: tagColor }}
                                  />
                                  {tagName}
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      if (!loading) handleRemoveAssignedTag(removeId);
                                    }}
                                    disabled={loading}
                                    aria-label={`Remove ${tagName} tag`}
                                    title="Remove tag"
                                    className="ml-0.5 inline-flex items-center justify-center rounded-full hover:bg-black/10 active:bg-black/20 transition-colors disabled:opacity-50 touch-manipulation"
                                    style={{ WebkitTapHighlightColor: 'transparent', width: 18, height: 18 }}
                                  >
                                    <X className="h-3 w-3" style={{ color: tagColor }} />
                                  </button>
                                </span>
                              );
                              })}
                            </div>
                          ) : (
                            <div className="text-sm text-gray-500">No tags assigned</div>
                          );
                        })()}
                      </div>
                    </div>
                  )}

                  {/* Orders tab (customers, Inventory app): store order history +
                      the order-related settings that used to live on Overview/Wallet. */}
                  {activeTab === 'orders' && !isSupplier && isInventoryAppInstalled && (
                    <>
                      {/* Containers first: drivers/staff check on-hand jugs before anything else.
                          The card renders nothing when the customer has no container activity. */}
                      {selectedBusiness?.id && customer?.id && (
                        <CustomerContainersCard
                          businessId={selectedBusiness.id}
                          customerId={customer.id}
                          onWalletChanged={() => {
                            loadWalletBalance();
                            loadWalletTransactions();
                          }}
                        />
                      )}

                      {/* Recurring rentals (renders nothing when the customer has none) */}
                      {selectedBusiness?.id && customer?.id && (
                        <CustomerRentalsCard
                          businessId={selectedBusiness.id}
                          customerId={customer.id}
                          zBase={100160}
                        />
                      )}

                      <div className="bg-white rounded-[12px] border border-gray-200/60 shadow-[0_1px_2px_rgba(0,0,0,0.05)] overflow-hidden">
                        <div className="bg-gray-50 border-b border-gray-200 px-6 py-4 flex justify-between items-center">
                          <div className="flex items-center space-x-3">
                            <h3 className="text-lg font-semibold text-gray-900">Order History</h3>
                            <span className="text-sm font-medium text-gray-500 bg-gray-100 rounded-xl px-2 py-1">
                              {customerStoreOrders.length}
                            </span>
                          </div>
                        </div>
                        <div className="p-6">
                          {/* Status filter — same options as the Orders page, "All" default */}
                          <div className="flex gap-1.5 overflow-x-auto pb-1 -mx-1 px-1 mb-4">
                            {STORE_ORDER_FILTERS.map(f => (
                              <button
                                key={f.id}
                                onClick={() => setStoreOrderFilter(f.id)}
                                className={`px-3 py-1.5 rounded-full text-[12px] font-semibold whitespace-nowrap transition-colors ${
                                  storeOrderFilter === f.id ? 'bg-blue-600 text-white' : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-50'
                                }`}
                              >
                                {f.label}
                              </button>
                            ))}
                          </div>
                          {loadingStoreOrders ? (
                            <div className="flex items-center justify-center py-12">
                              <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
                            </div>
                          ) : filteredStoreOrders.length === 0 ? (
                            <div className="py-8 text-center">
                              <Package className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                              <div className="text-sm font-semibold text-gray-500">
                                {customerStoreOrders.length === 0 ? 'No orders yet' : 'No orders match this filter'}
                              </div>
                              <div className="text-sm text-gray-400 mt-1">
                                {customerStoreOrders.length === 0
                                  ? 'Store orders for this customer will show here'
                                  : 'Try a different status filter'}
                              </div>
                            </div>
                          ) : (
                            <div className="space-y-2">
                              {filteredStoreOrders.map((order) => (
                                <OrderRow
                                  key={order.id}
                                  order={order}
                                  showCustomer={false}
                                  onClick={() => setSelectedStoreOrder(order)}
                                />
                              ))}
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="bg-white rounded-[12px] border border-gray-200/60 shadow-[0_1px_2px_rgba(0,0,0,0.05)] overflow-hidden">
                        <div className="bg-gray-50 border-b border-gray-200 px-6 py-4">
                          <h3 className="text-lg font-semibold text-gray-900">Store Billing</h3>
                          <p className="text-sm text-gray-500 mt-0.5">How this customer pays for online store orders</p>
                        </div>
                        <div className="p-6">
                          <div className="flex items-center justify-between gap-4">
                            <div>
                              <p className="text-sm font-medium text-gray-900">Invoice-only checkout</p>
                              <p className="text-xs text-gray-500 mt-1">
                                When on, their online orders skip pay-at-checkout and are invoiced when you deliver. Applies only when they're logged into their customer account — guests always pay at checkout.
                              </p>
                            </div>
                            <button
                              type="button"
                              onClick={() => handleInvoiceOnlyToggle(!((formData as any)?.invoice_only ?? (customer as any)?.invoice_only))}
                              disabled={savingInvoiceOnly}
                              className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors disabled:opacity-60 ${
                                ((formData as any)?.invoice_only ?? (customer as any)?.invoice_only) ? 'bg-blue-600' : 'bg-gray-300'
                              }`}
                              style={{ WebkitTapHighlightColor: 'transparent' }}
                              aria-pressed={((formData as any)?.invoice_only ?? (customer as any)?.invoice_only) === true}
                            >
                              <span
                                className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                                  ((formData as any)?.invoice_only ?? (customer as any)?.invoice_only) ? 'translate-x-6' : 'translate-x-1'
                                }`}
                              />
                            </button>
                          </div>

                          {/* Customer payment terms — invoices from their orders default to this */}
                          <div className="flex items-center justify-between gap-4 mt-5 pt-5 border-t border-gray-100">
                            <div>
                              <p className="text-sm font-medium text-gray-900">Payment terms</p>
                              <p className="text-xs text-gray-500 mt-1">
                                Invoices for this customer's orders default to these terms (e.g. Net 30). You can still override per invoice at Deliver &amp; Invoice.
                              </p>
                            </div>
                            <select
                              value={((formData as any)?.default_payment_terms ?? (customer as any)?.default_payment_terms) || 'default'}
                              onChange={(e) => handlePaymentTermsChange(e.target.value)}
                              className="flex-shrink-0 px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500"
                            >
                              <option value="default">Business default</option>
                              {DEFAULT_TERM_OPTIONS.map(o => (
                                <option key={o.value} value={o.value}>{o.label}</option>
                              ))}
                            </select>
                          </div>

                          {/* One-time email-verification waiver for portal signup/reset */}
                          <div className="flex items-center justify-between gap-4 mt-5 pt-5 border-t border-gray-100">
                            <div>
                              <p className="text-sm font-medium text-gray-900">Skip email verification</p>
                              <p className="text-xs text-gray-500 mt-1">
                                For customers who can't read the inbox on file (e.g. a company address). While on, their next store account signup or password reset skips the email code — it turns itself off as soon as it's used. Anyone who knows the email can claim the account while this is on, so only enable it when they're about to sign up.
                              </p>
                            </div>
                            <button
                              type="button"
                              onClick={() => handleVerificationExemptToggle(!((formData as any)?.portal_verification_exempt ?? (customer as any)?.portal_verification_exempt))}
                              disabled={savingVerificationExempt}
                              className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors disabled:opacity-60 ${
                                ((formData as any)?.portal_verification_exempt ?? (customer as any)?.portal_verification_exempt) ? 'bg-amber-500' : 'bg-gray-300'
                              }`}
                              style={{ WebkitTapHighlightColor: 'transparent' }}
                              aria-pressed={((formData as any)?.portal_verification_exempt ?? (customer as any)?.portal_verification_exempt) === true}
                            >
                              <span
                                className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                                  ((formData as any)?.portal_verification_exempt ?? (customer as any)?.portal_verification_exempt) ? 'translate-x-6' : 'translate-x-1'
                                }`}
                              />
                            </button>
                          </div>
                        </div>
                      </div>

                      {/* Customer pricing: price tier or individual custom prices */}
                      {selectedBusiness?.id && customer?.id && (
                        <CustomerPricingCard businessId={selectedBusiness.id} customerId={customer.id} />
                      )}

                    </>
                  )}

                  {activeTab === 'orders' && isSupplier && (
                    <div className="bg-white rounded-[12px] border border-gray-200/60 shadow-[0_1px_2px_rgba(0,0,0,0.05)] overflow-hidden">
                      <div className="bg-gray-50 border-b border-gray-200 px-6 py-4 flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <Package className="h-5 w-5 text-gray-600" />
                          <h3 className="text-lg font-semibold text-gray-900">Purchase orders</h3>
                          <span className="text-sm font-medium text-gray-500 bg-gray-100 rounded-xl px-2 py-1">
                            {supplierOrders.length}
                          </span>
                        </div>
                      </div>
                      <div className="p-6">
                        {loadingSupplierOrders ? (
                          <div className="flex items-center justify-center py-12">
                            <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
                          </div>
                        ) : supplierOrders.length === 0 ? (
                          <EmptyState
                            icon={<Package className="w-8 h-8 text-gray-400" />}
                            title="No purchase orders"
                            description="No POs have been placed with this supplier yet."
                          />
                        ) : (
                          <ul className="space-y-3">
                            {supplierOrders.map((order) => (
                              <li
                                key={order.id}
                                role="button"
                                tabIndex={0}
                                onClick={() => setSelectedOrderForModal(order)}
                                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setSelectedOrderForModal(order); } }}
                                className="flex items-center justify-between p-4 rounded-xl border border-gray-200/60 hover:bg-gray-50/80 transition-colors cursor-pointer"
                              >
                                <div className="flex items-center gap-3 min-w-0">
                                  <div className="h-10 w-10 rounded-lg bg-amber-100 flex items-center justify-center flex-shrink-0">
                                    <Package className="h-5 w-5 text-amber-700" />
                                  </div>
                                  <div className="min-w-0">
                                    <div className="font-semibold text-gray-900 truncate">{order.order_number}</div>
                                    <div className="text-sm text-gray-500 flex items-center gap-2 flex-wrap">
                                      {order.project?.title && (
                                        <span>{order.project.title}</span>
                                      )}
                                      <span>
                                        {order.created_at
                                          ? new Date(order.created_at).toLocaleDateString(undefined, {
                                              year: 'numeric',
                                              month: 'short',
                                              day: 'numeric'
                                            })
                                          : '—'}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                                <div className="flex items-center gap-3 flex-shrink-0">
                                  <span
                                    className={`text-sm font-medium px-2.5 py-1 rounded-full ${
                                      order.status === 'received'
                                        ? 'bg-green-100 text-green-800'
                                        : order.status === 'cancelled'
                                        ? 'bg-gray-100 text-gray-600'
                                        : 'bg-amber-100 text-amber-800'
                                    }`}
                                  >
                                    {order.status}
                                  </span>
                                  <span className="text-sm font-semibold text-gray-900">
                                    ${Number(order.total_amount ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                                  </span>
                                  <ChevronRight className="h-5 w-5 text-gray-400 flex-shrink-0" aria-hidden />
                                </div>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    </div>
                  )}

                  {activeTab === 'vehicles' && (
                    <div className="bg-white rounded-[12px] border border-gray-200/60 shadow-[0_1px_2px_rgba(0,0,0,0.05)] overflow-hidden">
                      <div className="bg-gray-50 border-b border-gray-200 px-6 py-4 flex justify-between items-center">
                        <div className="flex items-center space-x-3">
                          <h3 className="text-lg font-semibold text-gray-900">Vehicles</h3>
                          <span className="text-sm font-medium text-gray-500 bg-gray-100 rounded-xl px-2 py-1">
                            {customer.vehicles?.length || 0}
                          </span>
                        </div>
                        <button
                          onClick={() => setIsAddingVehicle(true)}
                          className="inline-flex items-center justify-center px-4 py-2 text-sm font-semibold text-blue-600 hover:text-blue-700 bg-blue-50 hover:bg-blue-100 active:bg-blue-200 rounded-xl transition-all duration-150 active:scale-[0.98] touch-manipulation"
                          style={{ WebkitTapHighlightColor: 'transparent' }}
                        >
                          <Plus className="h-4 w-4 mr-1.5" />
                          Add Vehicle
                        </button>
                      </div>
                      <div className="p-6">
                        {/* Add Vehicle opens the standard CreateVehicleModal
                            (with VIN decode) — mounted at the bottom of this modal. */}
                        <div className="space-y-4">
                          {customer.vehicles?.map((vehicle) => (
                            <VehicleInlineCard
                              key={vehicle.id}
                              vehicle={vehicle}
                              onSaved={() => onUpdate()}
                              onDelete={() => handleDeleteVehicle(vehicle.id)}
                              onError={(msg) => setError(msg)}
                            />
                          ))}

                          {customer.vehicles?.length === 0 && !isAddingVehicle && (
                            <div className="py-8 text-center">
                              <Car className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                              <div className="text-sm font-semibold text-gray-500">No vehicles added yet</div>
                              <div className="text-sm text-gray-400 mt-1">Add a vehicle to get started</div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {activeTab === 'jobs' && (
                    <div className="bg-white rounded-[12px] border border-gray-200/60 shadow-[0_1px_2px_rgba(0,0,0,0.05)] overflow-hidden">
                      <div className="bg-gray-50 border-b border-gray-200 px-6 py-4 flex justify-between items-center">
                        <div className="flex items-center space-x-3">
                          <h3 className="text-lg font-semibold text-gray-900">Booking History</h3>
                          <span className="text-sm font-medium text-gray-500 bg-gray-100 rounded-xl px-2 py-1">
                            {customerBookings.length + customerFieldQuotes.length}
                          </span>
                        </div>
                      </div>

                      <div className="p-6">
                        {/* Mobile Booking List */}
                        <div className="sm:hidden">
                          {loadingBookings ? (
                            <div className="flex items-center justify-center h-32">
                              <div className="text-center">
                                <div className="w-8 h-8 border-3 border-gray-200 border-t-blue-600 rounded-full animate-spin mx-auto"></div>
                                <p className="mt-4 text-sm font-semibold text-gray-600">Loading bookings...</p>
                              </div>
                            </div>
                          ) : customerBookings.length === 0 && customerFieldQuotes.length === 0 ? (
                            <div className="py-8 text-center">
                              <Calendar className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                              <div className="text-sm font-semibold text-gray-500">No bookings found</div>
                              <div className="text-sm text-gray-400 mt-1">This customer hasn't made any bookings yet</div>
                            </div>
                          ) : (
                            <div className="space-y-3">
                              {customerBookings.map((booking) => (
                                <div key={booking.id}>
                                  <div 
                                    className="p-4 bg-gray-50 rounded-lg border border-gray-200 hover:bg-gray-100 transition-colors cursor-pointer"
                                    onClick={() => {
                                      // Open the booking info modal in place (this modal
                                      // can be reached from many routes, so don't navigate).
                                      setSelectedBookingForDetail(booking);
                                      setIsBookingDetailOpen(true);
                                    }}
                                  >
                                    <div className="flex items-start space-x-3">
                                      <div className="h-10 w-10 rounded-full bg-gradient-to-b from-gray-300 to-gray-400 flex items-center justify-center text-sm font-bold text-white shadow-sm uppercase">
                                        {booking.customer?.first_name?.[0]}{booking.customer?.last_name?.[0]}
                                      </div>
                                      <div className="flex-1 min-w-0">
                                        <div className="flex items-center justify-between">
                                          <div className="text-sm font-bold text-gray-900 truncate">
                                            #{booking.booking_number}
                                          </div>
                                          <span className={`ml-2 px-3 py-1 text-xs font-medium rounded-xl ${getStatusColor(booking.status)}`}>
                                            {booking.status.charAt(0).toUpperCase() + booking.status.slice(1)}
                                          </span>
                                        </div>
                                        <div className="mt-2 text-sm text-gray-600">
                                          {booking.services?.map((service, index) => (
                                            <div key={index} className="truncate font-medium">
                                              {service.name}
                                              <span className="text-gray-400"> • </span>
                                              {service.vehicle_make} {service.vehicle_model}
                                            </div>
                                          ))}
                                        </div>
                                        <div className="mt-2 flex items-center justify-between">
                                          <div className="text-sm text-gray-500 font-medium">
                                            {booking.start_date && `${booking.start_date} ${booking.start_time}`}
                                          </div>
                                          <div className="text-sm font-bold text-gray-900">
                                            ${booking.total_amount?.toLocaleString() || '0.00'}
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              ))}

                              {customerFieldQuotes.map((fq) => (
                                <div key={fq.id}>
                                  <div
                                    className="p-4 bg-gray-50 rounded-lg border border-gray-200 hover:bg-gray-100 transition-colors cursor-pointer"
                                    onClick={() => {
                                      setSelectedFieldQuoteId(fq.id);
                                      setIsFieldQuoteDetailOpen(true);
                                    }}
                                  >
                                    <div className="flex items-start space-x-3">
                                      <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-orange-400 to-orange-600 flex items-center justify-center shadow-sm">
                                        <FileText className="h-5 w-5 text-white" />
                                      </div>
                                      <div className="flex-1 min-w-0">
                                        <div className="flex items-center justify-between">
                                          <div className="text-sm font-bold text-gray-900 truncate">
                                            {fq.is_measurement ? 'Field Measurements' : 'Field Quote'}
                                          </div>
                                          <span className={`ml-2 px-3 py-1 text-xs font-medium rounded-xl ${getStatusColor(fq.status.replace(' ', '_'))}`}>
                                            {fq.status.charAt(0).toUpperCase() + fq.status.slice(1)}
                                          </span>
                                        </div>
                                        <div className="mt-2 text-sm text-gray-600 truncate font-medium">
                                          {fq.project_title || fq.field_quote_address || '—'}
                                        </div>
                                        <div className="mt-2 text-sm text-gray-500 font-medium">
                                          {fq.start_date ? `${fq.start_date}${fq.start_time ? ` ${fq.start_time}` : ''}` : 'Not scheduled'}
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>

                        {/* Desktop Table */}
                        <div className="hidden sm:block">
                          {loadingBookings ? (
                            <div className="flex items-center justify-center h-32">
                              <div className="text-center">
                                <div className="w-8 h-8 border-3 border-gray-200 border-t-blue-600 rounded-full animate-spin mx-auto"></div>
                                <p className="mt-4 text-sm font-semibold text-gray-600">Loading bookings...</p>
                              </div>
                            </div>
                          ) : customerBookings.length === 0 && customerFieldQuotes.length === 0 ? (
                            <div className="py-8 text-center">
                              <Calendar className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                              <div className="text-sm font-semibold text-gray-500">No bookings found</div>
                              <div className="text-sm text-gray-400 mt-1">This customer hasn't made any bookings yet</div>
                            </div>
                          ) : (
                            <div className="overflow-hidden rounded-xl border border-gray-200/60">
                              <table className="min-w-full">
                                <thead>
                                  <tr className="bg-gray-50">
                                    <th className="px-4 py-3 text-left text-sm font-bold text-gray-700 uppercase tracking-wider">
                                      Booking #
                                    </th>
                                    <th className="px-4 py-3 text-left text-sm font-bold text-gray-700 uppercase tracking-wider">
                                      Services
                                    </th>
                                    <th className="px-4 py-3 text-left text-sm font-bold text-gray-700 uppercase tracking-wider">
                                      Date
                                    </th>
                                    <th className="px-4 py-3 text-left text-sm font-bold text-gray-700 uppercase tracking-wider">
                                      Status
                                    </th>
                                    <th className="px-4 py-3 text-left text-sm font-bold text-gray-700 uppercase tracking-wider">
                                      Amount
                                    </th>
                                  </tr>
                                </thead>
                                <tbody className="bg-white">
                                  {customerBookings.map((booking) => (
                                    <tr
                                      key={booking.id}
                                      className="border-t border-gray-200 hover:bg-gray-50 active:bg-blue-50 transition-all duration-150 cursor-pointer touch-manipulation"
                                      style={{ WebkitTapHighlightColor: 'transparent' }}
                                      onClick={() => {
                                        // Open the booking info modal in place (this modal
                                        // can be reached from many routes, so don't navigate).
                                        setSelectedBookingForDetail(booking);
                                        setIsBookingDetailOpen(true);
                                      }}
                                    >
                                      <td className="px-4 py-4 whitespace-nowrap text-sm font-bold text-gray-900">
                                        #{booking.booking_number}
                                      </td>
                                      <td className="px-4 py-4">
                                        <div className="space-y-1">
                                          {booking.services?.map((service, index) => (
                                            <div key={`${service.id}-${index}`} className="text-sm">
                                              <span className="font-semibold text-gray-900">
                                                {service.name}
                                              </span>
                                              <span className="text-gray-500 ml-1">
                                                ({service.vehicle_make} {service.vehicle_model})
                                              </span>
                                              {index < (booking.services?.length || 0) - 1 && <br />}
                                            </div>
                                          ))}
                                        </div>
                                      </td>
                                      <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-600">
                                        {booking.start_date &&
                                          `${booking.start_date} ${booking.start_time}`}
                                      </td>
                                      <td className="px-4 py-4 whitespace-nowrap">
                                        <span
                                          className={`px-3 py-1 inline-flex text-sm font-medium rounded-xl ${getStatusColor(
                                            booking.status
                                          )}`}
                                        >
                                          {booking.status.charAt(0).toUpperCase() +
                                            booking.status.slice(1)}
                                        </span>
                                      </td>
                                      <td className="px-4 py-4 whitespace-nowrap text-sm font-bold text-gray-900">
                                        ${booking.total_amount?.toLocaleString() || '0.00'}
                                      </td>
                                    </tr>
                                  ))}

                                  {customerFieldQuotes.map((fq) => (
                                    <tr
                                      key={fq.id}
                                      className="border-t border-gray-200 hover:bg-gray-50 active:bg-blue-50 transition-all duration-150 cursor-pointer touch-manipulation"
                                      style={{ WebkitTapHighlightColor: 'transparent' }}
                                      onClick={() => {
                                        setSelectedFieldQuoteId(fq.id);
                                        setIsFieldQuoteDetailOpen(true);
                                      }}
                                    >
                                      <td className="px-4 py-4 whitespace-nowrap text-sm font-bold text-gray-900">
                                        <span className="inline-flex items-center gap-1.5">
                                          <FileText className="h-4 w-4 text-orange-500" />
                                          {fq.is_measurement ? 'Measurement' : 'Field Quote'}
                                        </span>
                                      </td>
                                      <td className="px-4 py-4 text-sm text-gray-700">
                                        {fq.project_title || fq.field_quote_address || '—'}
                                      </td>
                                      <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-600">
                                        {fq.start_date
                                          ? `${fq.start_date}${fq.start_time ? ` ${fq.start_time}` : ''}`
                                          : 'Not scheduled'}
                                      </td>
                                      <td className="px-4 py-4 whitespace-nowrap">
                                        <span
                                          className={`px-3 py-1 inline-flex text-sm font-medium rounded-xl ${getStatusColor(
                                            fq.status.replace(' ', '_')
                                          )}`}
                                        >
                                          {fq.status.charAt(0).toUpperCase() + fq.status.slice(1)}
                                        </span>
                                      </td>
                                      <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-400">
                                        —
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {activeTab === 'billing' && customer?.id && selectedBusiness?.id && (
                    <CustomerBillingTab
                      businessId={selectedBusiness.id}
                      customerId={customer.id}
                      customerName={`${customer.first_name || ''} ${customer.last_name || ''}`.trim() || (customer as any).full_name || 'Customer'}
                    />
                  )}

                  {activeTab === 'proposals' && (
                    <div className="bg-white rounded-[12px] border border-gray-200/60 shadow-[0_1px_2px_rgba(0,0,0,0.05)] overflow-hidden">
                      <div className="bg-gray-50 border-b border-gray-200 px-6 py-4 flex justify-between items-center">
                        <div className="flex items-center space-x-3">
                          <h3 className="text-lg font-semibold text-gray-900">Proposal History</h3>
                          <span className="text-sm font-medium text-gray-500 bg-gray-100 rounded-xl px-2 py-1">
                            {customerProposals.length}
                          </span>
                        </div>
                      </div>

                      <div className="p-6">
                        {/* Mobile Proposal List */}
                        <div className="sm:hidden">
                          {loadingProposals ? (
                            <div className="flex items-center justify-center h-32">
                              <div className="text-center">
                                <div className="w-8 h-8 border-3 border-gray-200 border-t-blue-600 rounded-full animate-spin mx-auto"></div>
                                <p className="mt-4 text-sm font-semibold text-gray-600">Loading proposals...</p>
                              </div>
                            </div>
                          ) : customerProposals.length === 0 ? (
                            <div className="py-8 text-center">
                              <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                              <div className="text-sm font-semibold text-gray-500">No proposals found</div>
                              <div className="text-sm text-gray-400 mt-1">This customer hasn't received any proposals yet</div>
                            </div>
                          ) : (
                            <div className="space-y-3">
                              {customerProposals.map((proposal) => (
                                <div key={proposal.id}>
                                  <div 
                                    className="p-4 bg-gray-50 rounded-lg border border-gray-200 hover:bg-gray-100 transition-colors cursor-pointer"
                                    onClick={() => setSelectedProposalForModal(proposal)}
                                  >
                                    <div className="flex items-start space-x-3">
                                      <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center text-sm font-bold text-white shadow-sm">
                                        <FileText className="h-5 w-5" />
                                      </div>
                                      <div className="flex-1 min-w-0">
                                        <div className="flex items-center justify-between">
                                          <div className="text-sm font-bold text-gray-900 truncate">
                                            {proposal.title}
                                          </div>
                                          <span className={`ml-2 px-3 py-1 text-xs font-medium rounded-lg ${getProposalStatusColor(proposal.status)}`}>
                                            {proposal.status.charAt(0).toUpperCase() + proposal.status.slice(1)}
                                          </span>
                                        </div>
                                        <div className="mt-2 flex items-center justify-between">
                                          <div className="text-sm text-gray-500 font-medium">
                                            {new Date(proposal.created_at).toLocaleDateString()}
                                          </div>
                                          <div className="text-sm font-bold text-gray-900">
                                            ${proposal.total_amount.toLocaleString()}
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>

                        {/* Desktop Table */}
                        <div className="hidden sm:block">
                          {loadingProposals ? (
                            <div className="flex items-center justify-center h-32">
                              <div className="text-center">
                                <div className="w-8 h-8 border-3 border-gray-200 border-t-blue-600 rounded-full animate-spin mx-auto"></div>
                                <p className="mt-4 text-sm font-semibold text-gray-600">Loading proposals...</p>
                              </div>
                            </div>
                          ) : customerProposals.length === 0 ? (
                            <div className="py-8 text-center">
                              <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                              <div className="text-sm font-semibold text-gray-500">No proposals found</div>
                              <div className="text-sm text-gray-400 mt-1">This customer hasn't received any proposals yet</div>
                            </div>
                          ) : (
                            <div className="overflow-hidden rounded-xl border border-gray-200/60">
                              <table className="min-w-full">
                                <thead>
                                  <tr className="bg-gray-50">
                                    <th className="px-4 py-3 text-left text-sm font-bold text-gray-700 uppercase tracking-wider">
                                      Customer
                                    </th>
                                    <th className="px-4 py-3 text-left text-sm font-bold text-gray-700 uppercase tracking-wider">
                                      Title
                                    </th>
                                    <th className="px-4 py-3 text-left text-sm font-bold text-gray-700 uppercase tracking-wider">
                                      Status
                                    </th>
                                    <th className="px-4 py-3 text-left text-sm font-bold text-gray-700 uppercase tracking-wider">
                                      Amount
                                    </th>
                                  </tr>
                                </thead>
                                <tbody className="bg-white">
                                  {customerProposals.map((proposal) => (
                                    <tr
                                      key={proposal.id}
                                      className="border-t border-gray-200 hover:bg-gray-50 active:bg-blue-50 transition-all duration-150 cursor-pointer touch-manipulation"
                                      style={{ WebkitTapHighlightColor: 'transparent' }}
                                      onClick={() => setSelectedProposalForModal(proposal)}
                                    >
                                      <td className="px-4 py-4 whitespace-nowrap">
                                        <div className="flex items-center">
                                          <div className="h-10 w-10 rounded-full bg-gradient-to-b from-gray-300 to-gray-400 flex items-center justify-center text-sm font-bold text-white shadow-sm uppercase">
                                            {proposal.customer?.first_name?.[0]}{proposal.customer?.last_name?.[0]}
                                          </div>
                                          <div className="ml-4">
                                            <div className="text-sm font-bold text-gray-900">
                                              {proposal.customer?.first_name} {proposal.customer?.last_name}
                                            </div>
                                            <div className="text-sm text-gray-600 font-medium">
                                              {proposal.customer?.email}
                                            </div>
                                          </div>
                                        </div>
                                      </td>
                                      <td className="px-4 py-4 whitespace-nowrap text-sm font-bold text-gray-900">
                                        {proposal.title}
                                      </td>
                                      <td className="px-4 py-4 whitespace-nowrap">
                                        <span
                                          className={`px-3 py-1 inline-flex text-sm font-medium rounded-lg ${getProposalStatusColor(
                                            proposal.status
                                          )}`}
                                        >
                                          {proposal.status.charAt(0).toUpperCase() +
                                            proposal.status.slice(1)}
                                        </span>
                                      </td>
                                      <td className="px-4 py-4 whitespace-nowrap text-sm font-bold text-gray-900">
                                        ${proposal.total_amount.toLocaleString()}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {activeTab === 'leads' && isCrmAppInstalled && (
                    <div className="bg-white rounded-[12px] border border-gray-200/60 shadow-[0_1px_2px_rgba(0,0,0,0.05)] overflow-hidden">
                      <div className="bg-gray-50 border-b border-gray-200 px-6 py-4 flex justify-between items-center">
                        <div className="flex items-center space-x-3">
                          <h3 className="text-lg font-semibold text-gray-900">Leads</h3>
                          <span className="text-sm font-medium text-gray-500 bg-gray-100 rounded-xl px-2 py-1">
                            {customerOpportunities.length}
                          </span>
                        </div>
                      </div>

                      <div className="p-6">
                        {loadingOpportunities ? (
                          <div className="flex items-center justify-center h-32">
                            <div className="text-center">
                              <div className="w-8 h-8 border-3 border-gray-200 border-t-blue-600 rounded-full animate-spin mx-auto"></div>
                              <p className="mt-4 text-sm font-semibold text-gray-600">Loading leads...</p>
                            </div>
                          </div>
                        ) : customerOpportunities.length === 0 ? (
                          <div className="py-8 text-center">
                            <Users className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                            <div className="text-sm font-semibold text-gray-500">No leads found</div>
                            <div className="text-sm text-gray-400 mt-1">This customer has no CRM opportunities yet</div>
                          </div>
                        ) : (
                          <div className="space-y-3">
                            {customerOpportunities.map((opportunity) => {
                              const pipeline = pipelineInfo.get(opportunity.pipeline_id);
                              const stageName = pipeline?.stages.find((s) => s.id === opportunity.stage_id)?.name;
                              const pipelineLabel = [pipeline?.name, stageName].filter(Boolean).join(' · ');
                              return (
                                <div
                                  key={opportunity.id}
                                  className="p-4 bg-gray-50 rounded-lg border border-gray-200 hover:bg-gray-100 transition-colors cursor-pointer touch-manipulation"
                                  style={{ WebkitTapHighlightColor: 'transparent' }}
                                  onClick={() => setSelectedOpportunityId(opportunity.id)}
                                >
                                  <div className="flex items-start space-x-3">
                                    <div className="h-10 w-10 rounded-full bg-gradient-to-b from-gray-300 to-gray-400 flex items-center justify-center text-sm font-bold text-white shadow-sm uppercase">
                                      {customer?.first_name?.[0]}{customer?.last_name?.[0]}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <div className="flex items-center justify-between">
                                        <div className="text-sm font-bold text-gray-900 truncate">
                                          {opportunity.title || 'Lead'}
                                        </div>
                                        <span className={`ml-2 px-3 py-1 text-xs font-medium rounded-lg ${
                                          opportunity.status === 'Won'
                                            ? 'bg-emerald-100 text-emerald-800'
                                            : opportunity.status === 'Lost'
                                            ? 'bg-red-100 text-red-800'
                                            : 'bg-blue-100 text-blue-800'
                                        }`}>
                                          {opportunity.status || 'Open'}
                                        </span>
                                      </div>
                                      {pipelineLabel && (
                                        <div className="mt-1 text-xs text-gray-500 font-medium truncate">
                                          {pipelineLabel}
                                        </div>
                                      )}
                                      <div className="mt-2 flex items-center justify-between">
                                        <div className="text-sm text-gray-500 font-medium">
                                          {new Date(opportunity.created_at).toLocaleDateString()}
                                          {opportunity.source ? ` · ${opportunity.source}` : ''}
                                        </div>
                                        <div className="text-sm font-bold text-gray-900">
                                          ${(opportunity.value || 0).toLocaleString()}
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {activeTab === 'wallet' && (
                    <div className="space-y-6">
                      {showPoints && (
                        <div className="bg-white rounded-[12px] border border-gray-200/60 shadow-[0_1px_2px_rgba(0,0,0,0.05)] overflow-hidden">
                          <div className="bg-gray-50 border-b border-gray-200 px-6 py-4 flex justify-between items-center">
                            <div className="flex items-center space-x-3">
                              <Gift className="h-5 w-5 text-amber-600" />
                              <h3 className="text-lg font-semibold text-gray-900">Reward Points</h3>
                            </div>
                            <button
                              onClick={() => { setPointsMode('add'); setIsAdjustingPoints(true); }}
                              className="inline-flex items-center justify-center px-4 py-2 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-xl"
                            >
                              <Plus className="h-4 w-4 mr-1.5" />
                              Adjust Points
                            </button>
                          </div>
                          <div className="p-6 space-y-6">
                            {loadingPoints && pointsEntries.length === 0 ? (
                              <div className="flex items-center justify-center h-24">
                                <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
                              </div>
                            ) : (
                              <div className="bg-gradient-to-br from-amber-50 to-amber-100 rounded-xl p-6 border border-amber-200">
                                <p className="text-sm font-medium text-amber-700 mb-1">Points balance</p>
                                <p className="text-3xl font-bold text-amber-950">{pointsBalance.toLocaleString()}</p>
                              </div>
                            )}
                            {isAdjustingPoints && (
                              <div className="bg-gray-50 rounded-xl p-6 border border-gray-200 space-y-4">
                                <h4 className="text-lg font-semibold text-gray-900">Adjust Points</h4>
                                <div className="grid grid-cols-2 gap-2 p-1 bg-gray-200/60 rounded-xl">
                                  <button
                                    type="button"
                                    onClick={() => setPointsMode('add')}
                                    className={`py-2.5 rounded-lg text-sm font-semibold ${pointsMode === 'add' ? 'bg-white text-green-700 shadow-sm' : 'text-gray-600'}`}
                                  >
                                    Add
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => setPointsMode('deduct')}
                                    className={`py-2.5 rounded-lg text-sm font-semibold ${pointsMode === 'deduct' ? 'bg-white text-red-600 shadow-sm' : 'text-gray-600'}`}
                                  >
                                    Deduct
                                  </button>
                                </div>
                                <label className="block">
                                  <span className="block text-sm font-medium text-gray-700 mb-2">Points</span>
                                  <input
                                    type="number"
                                    min="1"
                                    step="1"
                                    value={pointsAmount}
                                    onChange={(e) => setPointsAmount(e.target.value)}
                                    placeholder="0"
                                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                  />
                                </label>
                                <label className="block">
                                  <span className="block text-sm font-medium text-gray-700 mb-2">Note (optional)</span>
                                  <input
                                    type="text"
                                    value={pointsNote}
                                    onChange={(e) => setPointsNote(e.target.value)}
                                    placeholder="Goodwill points"
                                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                  />
                                </label>
                                <div className="flex space-x-3">
                                  <button
                                    type="button"
                                    onClick={() => { setIsAdjustingPoints(false); setPointsAmount(''); setPointsNote(''); }}
                                    className="flex-1 px-4 py-3 text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 rounded-xl font-semibold"
                                  >
                                    Cancel
                                  </button>
                                  <button
                                    type="button"
                                    onClick={handleAdjustPoints}
                                    disabled={loadingPoints || !pointsAmount}
                                    className={`flex-1 px-4 py-3 text-white rounded-xl font-semibold disabled:opacity-60 ${pointsMode === 'deduct' ? 'bg-red-600 hover:bg-red-700' : 'bg-blue-600 hover:bg-blue-700'}`}
                                  >
                                    {pointsMode === 'deduct' ? 'Deduct Points' : 'Add Points'}
                                  </button>
                                </div>
                              </div>
                            )}
                            {pointsEntries.length > 0 && (
                              <div className="divide-y divide-gray-100 border border-gray-200 rounded-xl overflow-hidden">
                                {pointsEntries.map((entry) => {
                                  const label = entry.reason === 'refund' ? 'Refund adjustment' : entry.reason === 'adjust' ? 'Manual adjustment' : 'Earned';
                                  return (
                                    <div key={entry.id} className="flex items-center justify-between px-4 py-3">
                                      <div className="min-w-0">
                                        <p className="text-sm font-semibold text-gray-900 truncate">{entry.note || label}</p>
                                        <p className="text-xs text-gray-500">
                                          {label} · {new Date(entry.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                                        </p>
                                      </div>
                                      <span className={`text-sm font-bold ${entry.points < 0 ? 'text-red-600' : 'text-green-700'}`}>
                                        {entry.points > 0 ? '+' : ''}{entry.points.toLocaleString()}
                                      </span>
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                      {selectedBusiness?.id &&
                        customer?.id &&
                        selectedBusiness.stripe_connect_account_id && (
                        <CustomerSavedCardPanel
                          businessId={selectedBusiness.id}
                          customerId={customer.id}
                          stripeConnectReady={stripeConnectReadyForSavedCards}
                          customerEmail={basicInfoForDisplay?.email ?? customer.email}
                          customerName={`${basicInfoForDisplay?.first_name || ''} ${basicInfoForDisplay?.last_name || ''}`.trim() || undefined}
                        />
                      )}

                    <div className="bg-white rounded-[12px] border border-gray-200/60 shadow-[0_1px_2px_rgba(0,0,0,0.05)] overflow-hidden">
                      <div className="bg-gray-50 border-b border-gray-200 px-6 py-4 flex justify-between items-center">
                        <div className="flex items-center space-x-3">
                          <Wallet className="h-5 w-5 text-blue-600" />
                          <h3 className="text-lg font-semibold text-gray-900">Wallet Balance</h3>
                        </div>
                        <div className="flex items-center gap-2">
                          {walletBalance > 0 && (
                            <button
                              onClick={() => { setPayoutAmount(walletBalance.toFixed(2)); setIsPayoutOpen(true); }}
                              className="inline-flex items-center justify-center px-4 py-2 text-sm font-semibold text-gray-800 bg-white border border-gray-300 hover:bg-gray-50 active:bg-gray-100 rounded-xl transition-all duration-150 active:scale-[0.98] touch-manipulation"
                              style={{ WebkitTapHighlightColor: 'transparent' }}
                            >
                              <ArrowUpRight className="h-4 w-4 mr-1.5" />
                              Pay out
                            </button>
                          )}
                          <button
                            onClick={() => { setAdjustMode('add'); setIsAddingCredit(true); }}
                            className="inline-flex items-center justify-center px-4 py-2 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 active:bg-blue-800 rounded-xl transition-all duration-150 active:scale-[0.98] touch-manipulation"
                            style={{ WebkitTapHighlightColor: 'transparent' }}
                          >
                            <Plus className="h-4 w-4 mr-1.5" />
                            Adjust Balance
                          </button>
                        </div>
                      </div>

                      {/* Pay out wallet credit — real money leaves: back to a card they
                          paid with, cash, or e-transfer. Goes through the refund engine. */}
                      {isPayoutOpen && selectedBusiness?.id && customer?.id && (
                        <div className="border-b border-gray-200 bg-amber-50/40 px-6 py-4">
                          <div className="flex items-end gap-3">
                            <label className="flex-1">
                              <span className="block text-xs font-medium text-gray-600 mb-1">Amount to pay out</span>
                              <div className="relative">
                                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm">$</span>
                                <input
                                  type="number"
                                  min="0.01"
                                  step="0.01"
                                  max={walletBalance}
                                  value={payoutAmount}
                                  onChange={e => setPayoutAmount(e.target.value)}
                                  className="w-full pl-7 pr-3 py-2.5 text-sm border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                  style={{ fontSize: '16px' }}
                                />
                              </div>
                            </label>
                            <button
                              onClick={() => setIsPayoutOpen(false)}
                              className="px-4 py-2.5 text-sm font-semibold text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 rounded-xl"
                            >
                              Cancel
                            </button>
                            <button
                              onClick={() => setIsPayoutSheetOpen(true)}
                              disabled={!(parseFloat(payoutAmount) > 0) || parseFloat(payoutAmount) > walletBalance + 0.0001}
                              className="px-4 py-2.5 text-sm font-semibold text-white bg-gray-900 hover:bg-black disabled:bg-gray-300 rounded-xl"
                            >
                              Choose how…
                            </button>
                          </div>
                          <p className="mt-2 text-[12px] text-gray-500">
                            Up to ${walletBalance.toFixed(2)} on file. Card payouts go back onto a card this customer paid with here.
                          </p>
                        </div>
                      )}
                      {isPayoutSheetOpen && selectedBusiness?.id && customer?.id && (
                        <RefundSheet
                          open
                          onClose={() => setIsPayoutSheetOpen(false)}
                          businessId={selectedBusiness.id}
                          customerId={customer.id}
                          amountCents={Math.round((parseFloat(payoutAmount) || 0) * 100)}
                          title={`Pay out $${(parseFloat(payoutAmount) || 0).toFixed(2)}`}
                          description="Wallet credit leaves the wallet and goes back to the customer."
                          destinations={['card', 'cash', 'e_transfer']}
                          confirmLabel="Pay out"
                          onConfirm={async ({ destination, reference }) =>
                            refundCustomer({
                              businessId: selectedBusiness.id,
                              customerId: customer.id,
                              amountCents: Math.round((parseFloat(payoutAmount) || 0) * 100),
                              destination,
                              source: 'wallet_payout',
                              reason: 'Wallet credit paid out',
                              reference,
                            })
                          }
                          onDone={outcome => {
                            if (outcome) {
                              setIsPayoutOpen(false);
                              loadWalletBalance();
                              loadWalletTransactions();
                              toast.success(`Paid out — ${describeRefundOutcome(outcome)}`);
                            }
                          }}
                        />
                      )}

                      <div className="p-6">
                        {loadingWallet ? (
                          <div className="flex items-center justify-center h-32">
                            <div className="text-center">
                              <Loader2 className="h-8 w-8 animate-spin text-gray-400 mx-auto mb-2" />
                              <p className="text-sm font-semibold text-gray-600">Loading wallet...</p>
                            </div>
                          </div>
                        ) : (
                          <div className="space-y-6">
                            {/* Wallet Balance Display */}
                            <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-6 border border-blue-200">
                              <div className="flex items-center justify-between">
                                <div>
                                  <p className="text-sm font-medium text-blue-600 mb-1">Current Balance</p>
                                  <p className="text-3xl font-bold text-blue-900">
                                    ${walletBalance.toFixed(2)}
                                  </p>
                                </div>
                                <div className="h-16 w-16 bg-blue-600 rounded-full flex items-center justify-center">
                                  <Wallet className="h-8 w-8 text-white" />
                                </div>
                              </div>
                            </div>

                            {/* Add Credit Modal */}
                            {isAddingCredit && (
                              <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
                                <h4 className="text-lg font-semibold text-gray-900 mb-4">Adjust Wallet Balance</h4>
                                <div className="space-y-4">
                                  <div className="grid grid-cols-2 gap-2 p-1 bg-gray-200/60 rounded-xl">
                                    <button
                                      type="button"
                                      onClick={() => setAdjustMode('add')}
                                      className={`py-2.5 rounded-lg text-sm font-semibold transition-colors ${
                                        adjustMode === 'add' ? 'bg-white text-green-700 shadow-sm' : 'text-gray-600'
                                      }`}
                                    >
                                      Add Credit
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => setAdjustMode('deduct')}
                                      className={`py-2.5 rounded-lg text-sm font-semibold transition-colors ${
                                        adjustMode === 'deduct' ? 'bg-white text-red-600 shadow-sm' : 'text-gray-600'
                                      }`}
                                    >
                                      Deduct
                                    </button>
                                  </div>
                                  <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                      Amount
                                    </label>
                                    <div className="relative">
                                      <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                                      <input
                                        type="number"
                                        min="0"
                                        step="0.01"
                                        value={creditAmount}
                                        onChange={(e) => setCreditAmount(e.target.value)}
                                        placeholder="0.00"
                                        className="w-full pl-8 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                      />
                                    </div>
                                  </div>
                                  <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                      Note (Optional)
                                    </label>
                                    <input
                                      type="text"
                                      value={creditNote}
                                      onChange={(e) => setCreditNote(e.target.value)}
                                      placeholder={adjustMode === 'deduct' ? 'e.g., Correcting a duplicate credit' : 'e.g., Referral reward, Manual credit'}
                                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    />
                                  </div>
                                  <div className="flex space-x-3">
                                    <button
                                      onClick={() => {
                                        setIsAddingCredit(false);
                                        setCreditAmount('');
                                        setCreditNote('');
                                      }}
                                      className="flex-1 px-4 py-3 text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 rounded-xl font-semibold transition-colors"
                                    >
                                      Cancel
                                    </button>
                                    <button
                                      onClick={handleAdjustWallet}
                                      disabled={loadingWallet || !creditAmount || parseFloat(creditAmount) <= 0}
                                      className={`flex-1 px-4 py-3 text-white disabled:cursor-not-allowed rounded-xl font-semibold transition-colors flex items-center justify-center space-x-2 ${
                                        adjustMode === 'deduct'
                                          ? 'bg-red-600 hover:bg-red-700 disabled:bg-red-400'
                                          : 'bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400'
                                      }`}
                                    >
                                      {loadingWallet ? (
                                        <>
                                          <Loader2 className="h-4 w-4 animate-spin" />
                                          <span>Saving...</span>
                                        </>
                                      ) : (
                                        <span>{adjustMode === 'deduct' ? 'Deduct from Wallet' : 'Add Credit'}</span>
                                      )}
                                    </button>
                                  </div>
                                </div>
                              </div>
                            )}

                            {/* Wallet History */}
                            <div>
                              <div className="flex items-center justify-between mb-3">
                                <h4 className="text-sm font-semibold text-gray-700">Balance History</h4>
                                {walletTransactions.length > 0 && (
                                  <span className="text-xs font-medium text-gray-500 bg-gray-100 rounded-full px-2 py-0.5">
                                    {walletTransactions.length} {walletTransactions.length === 1 ? 'transaction' : 'transactions'}
                                  </span>
                                )}
                              </div>
                              {loadingTransactions ? (
                                <div className="flex items-center justify-center py-8">
                                  <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
                                </div>
                              ) : walletTransactions.length === 0 ? (
                                <div className="text-center py-8 text-gray-400">
                                  <Wallet className="h-8 w-8 mx-auto mb-2 opacity-40" />
                                  <p className="text-sm font-medium">No transactions yet</p>
                                </div>
                              ) : (
                                <div className="divide-y divide-gray-100 border border-gray-200 rounded-xl overflow-hidden">
                                  {walletTransactions.map((tx) => {
                                    const amount = parseFloat(tx.amount) || 0;
                                    const typeConfig: Record<string, { icon: React.ReactNode; label: string; color: string }> = {
                                      manual: {
                                        icon: <DollarSign className="h-4 w-4" />,
                                        label: amount < 0 ? 'Manual Deduction' : 'Manual Credit',
                                        color: 'text-blue-600 bg-blue-50',
                                      },
                                      referral: {
                                        icon: <Users className="h-4 w-4" />,
                                        label: 'Referral Reward',
                                        color: 'text-purple-600 bg-purple-50',
                                      },
                                      subscription: {
                                        icon: <RefreshCw className="h-4 w-4" />,
                                        label: 'Subscription Credit',
                                        color: 'text-green-600 bg-green-50',
                                      },
                                      topup: {
                                        icon: <CreditCard className="h-4 w-4" />,
                                        label: 'Card Top-up',
                                        color: 'text-green-600 bg-green-50',
                                      },
                                      bonus: {
                                        icon: <Gift className="h-4 w-4" />,
                                        label: 'Top-up Bonus',
                                        color: 'text-amber-600 bg-amber-50',
                                      },
                                      redemption: {
                                        icon: <Wallet className="h-4 w-4" />,
                                        label: amount < 0 ? 'Spent on Order' : 'Refunded to Wallet',
                                        color: 'text-gray-600 bg-gray-100',
                                      },
                                    };
                                    const config = typeConfig[tx.type] || typeConfig.manual;

                                    return (
                                      <div key={tx.id} className="flex items-center justify-between px-4 py-3 hover:bg-gray-50 transition-colors">
                                        <div className="flex items-center space-x-3 min-w-0">
                                          <div className={`flex-shrink-0 h-8 w-8 rounded-full flex items-center justify-center ${config.color}`}>
                                            {config.icon}
                                          </div>
                                          <div className="min-w-0">
                                            <p className="text-sm font-semibold text-gray-900 truncate">
                                              {tx.description || config.label}
                                            </p>
                                            <p className="text-xs text-gray-500">
                                              {config.label} &middot; {new Date(tx.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })}
                                            </p>
                                          </div>
                                        </div>
                                        <div className="flex-shrink-0 ml-3">
                                          <span className={`text-sm font-bold ${amount < 0 ? 'text-red-600' : 'text-green-600'}`}>
                                            {amount < 0 ? '−' : '+'}${Math.abs(amount).toFixed(2)}
                                          </span>
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                    </div>
                  )}

                  {activeTab === 'gift_cards' && isGiftCardsAppInstalled && (
                    <div className="bg-white rounded-[12px] border border-gray-200/60 shadow-[0_1px_2px_rgba(0,0,0,0.05)] overflow-hidden">
                      <div className="bg-gray-50 border-b border-gray-200 px-6 py-4 flex justify-between items-center">
                        <div className="flex items-center space-x-3">
                          <h3 className="text-lg font-semibold text-gray-900">Gift Card History</h3>
                          <span className="text-sm font-medium text-gray-500 bg-gray-100 rounded-xl px-2 py-1">
                            {customerGiftCards.length}
                          </span>
                        </div>
                      </div>

                      <div className="p-6">
                        {loadingGiftCards ? (
                          <div className="flex items-center justify-center h-32">
                            <div className="text-center">
                              <div className="w-8 h-8 border-3 border-gray-200 border-t-blue-600 rounded-full animate-spin mx-auto"></div>
                              <p className="mt-4 text-sm font-semibold text-gray-600">Loading gift cards...</p>
                            </div>
                          </div>
                        ) : customerGiftCards.length === 0 ? (
                          <div className="py-8 text-center">
                            <Gift className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                            <div className="text-sm font-semibold text-gray-500">No gift cards found</div>
                            <div className="text-sm text-gray-400 mt-1">This customer hasn't purchased any gift cards yet</div>
                          </div>
                        ) : (
                          <div className="overflow-hidden rounded-lg border border-gray-200">
                            <table className="min-w-full">
                              <thead>
                                <tr className="bg-gray-50">
                                  <th className="px-4 py-3 text-left text-sm font-bold text-gray-700 uppercase tracking-wider">
                                    Code
                                  </th>
                                  <th className="px-4 py-3 text-left text-sm font-bold text-gray-700 uppercase tracking-wider">
                                    Recipient
                                  </th>
                                  <th className="px-4 py-3 text-left text-sm font-bold text-gray-700 uppercase tracking-wider">
                                    Initial Amount
                                  </th>
                                  <th className="px-4 py-3 text-left text-sm font-bold text-gray-700 uppercase tracking-wider">
                                    Current Balance
                                  </th>
                                  <th className="px-4 py-3 text-left text-sm font-bold text-gray-700 uppercase tracking-wider">
                                    Status
                                  </th>
                                  <th className="px-4 py-3 text-left text-sm font-bold text-gray-700 uppercase tracking-wider">
                                    Created
                                  </th>
                                </tr>
                              </thead>
                              <tbody className="bg-white divide-y divide-gray-200">
                                {customerGiftCards.map((card) => (
                                  <tr key={card.id} className="hover:bg-gray-50">
                                    <td className="px-4 py-4 whitespace-nowrap">
                                      <div className="flex items-center">
                                        <CreditCard className="h-4 w-4 text-blue-600 mr-2" />
                                        <span className="text-sm font-mono font-medium text-gray-900">
                                          {card.code}
                                        </span>
                                      </div>
                                    </td>
                                    <td className="px-4 py-4 text-sm text-gray-900">
                                      {card.recipient_name ? (
                                        <div>
                                          <div className="font-medium text-gray-900">{card.recipient_name}</div>
                                          {card.recipient_email && (
                                            <div className="text-gray-500 text-xs">{card.recipient_email}</div>
                                          )}
                                        </div>
                                      ) : (
                                        <span className="text-gray-400">No recipient</span>
                                      )}
                                    </td>
                                    <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                      ${card.initial_amount.toFixed(2)}
                                    </td>
                                    <td className="px-4 py-4 whitespace-nowrap text-sm">
                                      <div className="flex items-center space-x-2">
                                        <span className={`font-bold ${
                                          card.current_balance === 0 ? 'text-gray-400' : 'text-green-600'
                                        }`}>
                                          ${card.current_balance.toFixed(2)}
                                        </span>
                                        {card.current_balance < card.initial_amount && card.current_balance > 0 && (
                                          <span className="text-xs text-gray-500">
                                            (${(card.initial_amount - card.current_balance).toFixed(2)} used)
                                          </span>
                                        )}
                                      </div>
                                    </td>
                                    <td className="px-4 py-4 whitespace-nowrap">
                                      <span
                                        className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                          card.status === 'active'
                                            ? 'bg-green-100 text-green-800'
                                            : card.status === 'redeemed'
                                            ? 'bg-gray-100 text-gray-800'
                                            : 'bg-red-100 text-red-800'
                                        }`}
                                      >
                                        {card.status.charAt(0).toUpperCase() + card.status.slice(1)}
                                      </span>
                                    </td>
                                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                                      {new Date(card.created_at).toLocaleDateString()}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}

                        {/* Summary Stats */}
                        {customerGiftCards.length > 0 && (
                          <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-4">
                            <div className="bg-blue-50 rounded-xl p-4 border border-blue-200">
                              <div className="flex items-center space-x-2">
                                <Gift className="h-5 w-5 text-blue-600" />
                                <div>
                                  <div className="text-xs text-blue-600 font-medium">Total Purchased</div>
                                  <div className="text-lg font-bold text-blue-900">
                                    ${customerGiftCards.reduce((sum, card) => sum + card.initial_amount, 0).toFixed(2)}
                                  </div>
                                </div>
                              </div>
                            </div>
                            <div className="bg-green-50 rounded-xl p-4 border border-green-200">
                              <div className="flex items-center space-x-2">
                                <DollarSign className="h-5 w-5 text-green-600" />
                                <div>
                                  <div className="text-xs text-green-600 font-medium">Current Balance</div>
                                  <div className="text-lg font-bold text-green-900">
                                    ${customerGiftCards.reduce((sum, card) => sum + card.current_balance, 0).toFixed(2)}
                                  </div>
                                </div>
                              </div>
                            </div>
                            <div className="bg-blue-50 rounded-xl p-4 border border-blue-200">
                              <div className="flex items-center space-x-2">
                                <CreditCard className="h-5 w-5 text-blue-600" />
                                <div>
                                  <div className="text-xs text-blue-600 font-medium">Active Cards</div>
                                  <div className="text-lg font-bold text-blue-900">
                                    {customerGiftCards.filter(c => c.status === 'active').length}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {activeTab === 'subscriptions' && isServicesAppInstalled && subscriptionsEnabled && (
                    <div className="bg-white rounded-[12px] border border-gray-200/60 shadow-[0_1px_2px_rgba(0,0,0,0.05)] overflow-hidden">
                      <div className="bg-gray-50 border-b border-gray-200 px-6 py-4 flex justify-between items-center">
                        <div className="flex items-center space-x-3">
                          <h3 className="text-lg font-semibold text-gray-900">Subscriptions</h3>
                          <span className="text-sm font-medium text-gray-500 bg-gray-100 rounded-xl px-2 py-1">
                            {customerSubscriptions.length}
                          </span>
                        </div>
                      </div>

                      <div className="p-6">
                        {loadingSubscriptions ? (
                          <div className="flex items-center justify-center h-32">
                            <div className="text-center">
                              <div className="w-8 h-8 border-3 border-gray-200 border-t-blue-600 rounded-full animate-spin mx-auto"></div>
                              <p className="mt-4 text-sm font-semibold text-gray-600">Loading subscriptions...</p>
                            </div>
                          </div>
                        ) : customerSubscriptions.length === 0 ? (
                          <div className="py-8 text-center">
                            <DollarSign className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                            <div className="text-sm font-semibold text-gray-500">No subscriptions found</div>
                            <div className="text-sm text-gray-400 mt-1">This customer doesn't have any subscriptions yet</div>
                          </div>
                        ) : (
                          <div className="overflow-hidden rounded-lg border border-gray-200">
                            <table className="min-w-full">
                              <thead>
                                <tr className="bg-gray-50">
                                  <th className="px-4 py-3 text-left text-sm font-bold text-gray-700 uppercase tracking-wider">Name</th>
                                  <th className="px-4 py-3 text-left text-sm font-bold text-gray-700 uppercase tracking-wider">Amount</th>
                                  <th className="px-4 py-3 text-left text-sm font-bold text-gray-700 uppercase tracking-wider">Frequency</th>
                                  <th className="px-4 py-3 text-left text-sm font-bold text-gray-700 uppercase tracking-wider">Status</th>
                                  <th className="px-4 py-3 text-left text-sm font-bold text-gray-700 uppercase tracking-wider">Created</th>
                                </tr>
                              </thead>
                              <tbody className="bg-white divide-y divide-gray-200">
                                {customerSubscriptions.map((sub) => {
                                  const freqLabel = sub.interval_count === 1
                                    ? (sub.interval === 'day' ? 'Daily' : sub.interval === 'week' ? 'Weekly' : sub.interval === 'month' ? 'Monthly' : 'Yearly')
                                    : `Every ${sub.interval_count} ${sub.interval}s`;
                                  return (
                                    <tr key={sub.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => setSelectedSubscriptionForModal(sub)}>
                                      <td className="px-4 py-4 whitespace-nowrap">
                                        <div className="text-sm font-medium text-gray-900">{sub.name}</div>
                                        {sub.description && (
                                          <div className="text-xs text-gray-500 mt-0.5 truncate max-w-[200px]">{sub.description}</div>
                                        )}
                                      </td>
                                      <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                        ${Number(sub.amount).toFixed(2)}
                                        <span className="text-xs text-gray-400 ml-1 uppercase">{sub.currency}</span>
                                      </td>
                                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-700">
                                        {freqLabel}
                                      </td>
                                      <td className="px-4 py-4 whitespace-nowrap">
                                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                          sub.status === 'active'
                                            ? 'bg-green-100 text-green-800'
                                            : sub.status === 'pending'
                                            ? 'bg-yellow-100 text-yellow-800'
                                            : sub.status === 'cancelled'
                                            ? 'bg-red-100 text-red-800'
                                            : 'bg-gray-100 text-gray-800'
                                        }`}>
                                          {sub.status.charAt(0).toUpperCase() + sub.status.slice(1)}
                                        </span>
                                      </td>
                                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                                        {new Date(sub.created_at).toLocaleDateString()}
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Purchase order detail modal (when a PO is clicked in the Orders tab) */}
        {selectedOrderForModal && (
          <PurchaseOrderDetailModal
            order={selectedOrderForModal}
            onClose={() => setSelectedOrderForModal(null)}
            onOrderUpdated={loadSupplierOrders}
            business={selectedBusiness ? { name: selectedBusiness.name, address: selectedBusiness.address ?? null, phone: selectedBusiness.phone ?? null, email: selectedBusiness.email ?? null } : null}
          />
        )}

        {/* Add vehicle — standard create-vehicle modal (with VIN decode) */}
        {customer && (
          <CreateVehicleModal
            isOpen={isAddingVehicle}
            onClose={() => setIsAddingVehicle(false)}
            customerId={customer.id}
            onSuccess={() => {
              setIsAddingVehicle(false);
              onUpdate();
            }}
          />
        )}

        {/* Proposal details modal (when a proposal is clicked in the Proposals tab) */}
        {selectedProposalForModal && (
          <ProposalDetailsModal
            isOpen={!!selectedProposalForModal}
            onClose={() => setSelectedProposalForModal(null)}
            proposal={selectedProposalForModal}
            onUpdate={(updatedProposal) => {
              setCustomerProposals(prev => prev.map(p => p.id === updatedProposal.id ? updatedProposal : p));
            }}
          />
        )}

        {/* CRM opportunity drawer (when a lead is clicked in the Leads tab) */}
        {selectedOpportunityId != null && (
          <CrmEmbeddedOpportunityModal
            opportunityId={selectedOpportunityId}
            onClose={() => setSelectedOpportunityId(null)}
            onUpdated={() => loadCustomerOpportunities()}
          />
        )}

        {isFieldQuoteDetailOpen && (
          <FieldQuoteDetailModal
            isOpen={isFieldQuoteDetailOpen}
            fieldQuoteId={selectedFieldQuoteId}
            onClose={() => {
              setIsFieldQuoteDetailOpen(false);
              setSelectedFieldQuoteId(null);
            }}
            onUpdated={() => loadCustomerFieldQuotes()}
          />
        )}

        {isBookingDetailOpen && selectedBookingForDetail && (
          <BookingInfoModal
            isOpen={isBookingDetailOpen}
            booking={selectedBookingForDetail}
            zIndex={zIndex + 10}
            onClose={() => {
              setIsBookingDetailOpen(false);
              setSelectedBookingForDetail(null);
              loadCustomerBookings();
            }}
            onUpdate={() => loadCustomerBookings()}
          />
        )}

        {/* Store order detail (when an order is clicked in the Orders tab) */}
        {selectedStoreOrder && selectedBusiness?.id && (
          <React.Suspense fallback={null}>
            <OrderDetailModalLazy
              order={selectedStoreOrder}
              businessId={selectedBusiness.id}
              zBase={zIndex + 40}
              onClose={() => {
                setSelectedStoreOrder(null);
                loadCustomerStoreOrders();
              }}
              onChanged={loadCustomerStoreOrders}
              onInvoiced={() => {
                loadCustomerStoreOrders();
                loadWalletBalance();
                loadWalletTransactions();
              }}
            />
          </React.Suspense>
        )}

        {/* Edit subscription modal */}
        <EditSubscriptionModal
          isOpen={!!selectedSubscriptionForModal}
          onClose={() => setSelectedSubscriptionForModal(null)}
          subscription={selectedSubscriptionForModal}
          onUpdate={() => {
            loadCustomerSubscriptions();
            setSelectedSubscriptionForModal(null);
          }}
        />
      </div> {/* Close modal container wrapper */}
    </>
  );

  // 🚀 FIX: Use React Portal to render modal outside parent DOM tree
  // This prevents PWA stacking context issues and ensures modal appears above navigation
  // Provide the modal-stack context so ANY modal opened from inside here
  // (invoice viewer, receive payment, etc.) automatically stacks above this
  // one — no hardcoded z-index changes needed anywhere else.
  return createPortal(
    <ModalStackProvider zIndex={zIndex}>{modalContent}</ModalStackProvider>,
    document.body,
  );
}

// ── Vehicle card with live-saving fields ─────────────────────────────────────
// Every field is an input; a change is written when the field loses focus (or
// Enter is pressed), with a debounce as a backstop. Only changed fields are sent.
type VehicleDraft = {
  make: string; model: string; year: string; color: string; vin: string;
  license_plate: string; mileage: string; notes: string; is_primary: boolean;
};

function vehicleDraftFrom(v: CustomerVehicle): VehicleDraft {
  return {
    make: v.make || '',
    model: v.model || '',
    year: v.year ? String(v.year) : '',
    color: v.color || '',
    vin: v.vin || '',
    license_plate: v.license_plate || '',
    mileage: v.mileage != null ? String(v.mileage) : '',
    notes: v.notes || '',
    is_primary: !!v.is_primary,
  };
}

function VehicleInlineCard({
  vehicle,
  onSaved,
  onDelete,
  onError,
}: {
  vehicle: CustomerVehicle;
  onSaved: () => void;
  onDelete: () => void;
  onError: (message: string) => void;
}) {
  const [draft, setDraft] = useState<VehicleDraft>(() => vehicleDraftFrom(vehicle));
  const savedRef = useRef<VehicleDraft>(vehicleDraftFrom(vehicle));
  const draftRef = useRef(draft);
  draftRef.current = draft;
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const callbacksRef = useRef({ onSaved, onError });
  callbacksRef.current = { onSaved, onError };
  const [state, setState] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [fieldError, setFieldError] = useState<string | null>(null);

  // Take server changes (e.g. primary moved to another vehicle) when not mid-edit.
  useEffect(() => {
    const incoming = vehicleDraftFrom(vehicle);
    const dirty = JSON.stringify(draftRef.current) !== JSON.stringify(savedRef.current);
    if (!dirty) {
      savedRef.current = incoming;
      setDraft(incoming);
    }
  }, [vehicle]);

  useEffect(() => {
    if (state !== 'saved') return;
    const t = setTimeout(() => setState('idle'), 2000);
    return () => clearTimeout(t);
  }, [state]);

  const flush = useCallback(async () => {
    if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
    const cur = draftRef.current;
    const prev = savedRef.current;
    const patch: Partial<CustomerVehicle> = {};
    let problem: string | null = null;
    (Object.keys(cur) as (keyof VehicleDraft)[]).forEach((key) => {
      if (cur[key] === prev[key]) return;
      switch (key) {
        case 'make':
        case 'model':
          if (!cur[key].trim()) { problem = `${key === 'make' ? 'Make' : 'Model'} is required`; return; }
          patch[key] = cur[key].trim();
          break;
        case 'year': {
          const n = parseInt(cur.year, 10);
          if (!Number.isFinite(n) || n < 1900 || n > new Date().getFullYear() + 1) { problem = 'Enter a valid year'; return; }
          patch.year = n;
          break;
        }
        case 'mileage': {
          if (!cur.mileage.trim()) { patch.mileage = null; break; }
          const n = parseInt(cur.mileage, 10);
          if (!Number.isFinite(n) || n < 0) { problem = 'Enter a valid mileage'; return; }
          patch.mileage = n;
          break;
        }
        case 'color':
        case 'vin':
        case 'license_plate':
        case 'notes':
          patch[key] = cur[key].trim() || null;
          break;
        case 'is_primary':
          patch.is_primary = cur.is_primary;
          break;
      }
    });
    setFieldError(problem);
    if (Object.keys(patch).length === 0) return;
    try {
      setState('saving');
      await updateCustomerVehicle(vehicle.id, patch);
      savedRef.current = { ...prev, ...Object.fromEntries((Object.keys(patch) as (keyof VehicleDraft)[]).map((k) => [k, cur[k]])) } as VehicleDraft;
      setState('saved');
      callbacksRef.current.onSaved();
    } catch (err) {
      console.error('Error updating vehicle:', err);
      setState('error');
      callbacksRef.current.onError(err instanceof Error ? err.message : 'Failed to update vehicle');
    }
  }, [vehicle.id]);

  const edit = (patch: Partial<VehicleDraft>, immediate = false) => {
    setDraft((d) => ({ ...d, ...patch }));
    if (timerRef.current) clearTimeout(timerRef.current);
    if (immediate) {
      // Let the state settle, then write.
      timerRef.current = setTimeout(() => { void flush(); }, 0);
    } else {
      timerRef.current = setTimeout(() => { void flush(); }, 1200);
    }
  };

  useEffect(() => () => { if (timerRef.current) { clearTimeout(timerRef.current); void flush(); } }, [flush]);

  const input = 'w-full px-3 py-2 text-sm bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-150';
  const onEnter = (e: React.KeyboardEvent<HTMLInputElement>) => { if (e.key === 'Enter') e.currentTarget.blur(); };

  return (
    <div className="p-4 bg-white rounded-[12px] border border-gray-200/60 shadow-[0_1px_2px_rgba(0,0,0,0.05)]" onBlur={() => { void flush(); }}>
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <div className="text-sm font-bold text-gray-900 truncate">
              {[draft.year, draft.make, draft.model].filter(Boolean).join(' ') || 'Vehicle'}
            </div>
            {draft.is_primary && (
              <span className="px-2 py-0.5 text-xs font-medium rounded-xl bg-blue-100 text-blue-700">Primary</span>
            )}
          </div>
          <div className="text-xs text-gray-500 mt-0.5 inline-flex items-center gap-1.5 min-h-[16px]">
            {state === 'saving' && (<><Loader2 className="h-3 w-3 animate-spin" /> Saving…</>)}
            {state === 'saved' && <span className="text-green-600">Saved</span>}
            {state === 'error' && <span className="text-red-600">Not saved</span>}
            {state === 'idle' && (fieldError ? <span className="text-red-600">{fieldError}</span> : 'Changes save as you go')}
          </div>
        </div>
        <button
          type="button"
          onClick={onDelete}
          className="flex items-center justify-center w-9 h-9 rounded-xl text-red-500 hover:text-red-700 hover:bg-red-50 active:bg-red-100 active:scale-95 transition-all duration-150 touch-manipulation shrink-0"
          style={{ WebkitTapHighlightColor: 'transparent' }}
          aria-label="Delete vehicle"
        >
          <Trash2 className="h-4 w-4" />
        </button>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        <div>
          <label className="block text-xs font-semibold text-gray-600 mb-1">Year</label>
          <input type="number" inputMode="numeric" min="1900" max={new Date().getFullYear() + 1} value={draft.year} onChange={(e) => edit({ year: e.target.value })} onKeyDown={onEnter} className={input} style={{ fontSize: '16px' }} />
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-600 mb-1">Make</label>
          <input type="text" value={draft.make} onChange={(e) => edit({ make: e.target.value })} onKeyDown={onEnter} className={input} style={{ fontSize: '16px' }} />
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-600 mb-1">Model</label>
          <input type="text" value={draft.model} onChange={(e) => edit({ model: e.target.value })} onKeyDown={onEnter} className={input} style={{ fontSize: '16px' }} />
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-600 mb-1">Color</label>
          <input type="text" value={draft.color} onChange={(e) => edit({ color: e.target.value })} onKeyDown={onEnter} className={input} style={{ fontSize: '16px' }} />
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-600 mb-1">License Plate</label>
          <input type="text" value={draft.license_plate} onChange={(e) => edit({ license_plate: e.target.value })} onKeyDown={onEnter} className={input} style={{ fontSize: '16px' }} />
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-600 mb-1">Mileage</label>
          <input type="number" inputMode="numeric" min="0" value={draft.mileage} onChange={(e) => edit({ mileage: e.target.value })} onKeyDown={onEnter} className={input} style={{ fontSize: '16px' }} />
        </div>
        <div className="col-span-2 sm:col-span-3">
          <label className="block text-xs font-semibold text-gray-600 mb-1">VIN</label>
          <input type="text" value={draft.vin} onChange={(e) => edit({ vin: e.target.value })} onKeyDown={onEnter} className={`${input} font-mono uppercase`} style={{ fontSize: '16px' }} />
        </div>
        <div className="col-span-2 sm:col-span-3">
          <label className="block text-xs font-semibold text-gray-600 mb-1">Notes</label>
          <textarea value={draft.notes} onChange={(e) => edit({ notes: e.target.value })} rows={2} className={`${input} resize-none`} style={{ fontSize: '16px' }} />
        </div>
        <div className="col-span-2 sm:col-span-3 flex items-center justify-between">
          <label className="flex items-center gap-2.5 cursor-pointer">
            <input type="checkbox" checked={draft.is_primary} onChange={(e) => edit({ is_primary: e.target.checked }, true)} className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
            <span className="text-sm font-medium text-gray-700">Primary vehicle</span>
          </label>
          {vehicle.last_service && (
            <span className="text-xs text-gray-500">Last service {new Date(vehicle.last_service).toLocaleDateString()}</span>
          )}
        </div>
      </div>
    </div>
  );
}
